import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { getClientPhaseProgress } from '../utils/clientPhaseProgress';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { Brain, LogOut, Plus, Calendar, TrendingUp, Trash2, Lock, FolderOpen, MessageSquare, MessageCircle, Eye, Calendar as CalendarIcon, AlertCircle, XCircle, Star, Clock, Volume2, StopCircle, Video, IndianRupee, CheckCircle, BookOpen, Loader2 } from 'lucide-react';
import NotificationModal from '../components/NotificationModal';
import { useNotification } from '../hooks/useNotification';
import Pagination from '../components/Pagination';
import { usePagination } from '../hooks/usePagination';
import AccountSettings from '../components/AccountSettings';
import ConsentModal from '../components/ConsentModal';
import JitsiMeetVideoCallComponent from '../components/JitsiMeetVideoCallComponent';
import WebRTCVideoCall from '../components/WebRTCVideoCall';
import VideoCallRingModal from '../components/VideoCallRingModal';
import DPDPAVideoCallConsent from '../components/DPDPAVideoCallConsent';

import { withDoctorTitle } from '../utils/doctorTitle';
// Star Rating Component
const StarRating = ({ rating, totalRatings }) => {
  const stars = [];
  const fullStars = Math.floor(rating);
  const hasHalfStar = rating % 1 >= 0.5;
  
  // Full stars
  for (let i = 0; i < fullStars; i++) {
    stars.push(
      <Star key={`full-${i}`} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
    );
  }
  
  // Half star
  if (hasHalfStar && fullStars < 5) {
    stars.push(
      <div key="half" className="relative w-4 h-4">
        <Star className="w-4 h-4 text-gray-300 absolute" />
        <div className="overflow-hidden w-2 absolute">
          <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
        </div>
      </div>
    );
  }
  
  // Empty stars
  const emptyStars = 5 - Math.ceil(rating);
  for (let i = 0; i < emptyStars; i++) {
    stars.push(
      <Star key={`empty-${i}`} className="w-4 h-4 text-gray-300" />
    );
  }
  
  return (
    <div className="flex items-center gap-2">
      <div className="flex items-center gap-0.5">
        {stars}
      </div>
      <span className="text-sm text-gray-600">
        {rating > 0 ? `${rating.toFixed(1)} (${totalRatings} ${totalRatings === 1 ? 'review' : 'reviews'})` : 'No reviews yet'}
      </span>
    </div>
  );
};

const ClientDashboard = () => {
  const { notification, showSuccess, showError, showInfo, showWarning, closeNotification } = useNotification();
  const { user, logout } = useContext(AuthContext);
  
  // Consent Modal State
  const [showConsentModal, setShowConsentModal] = useState(false);
  
  const [currentPsychologist, setCurrentPsychologist] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [creatingSession, setCreatingSession] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackCategory, setFeedbackCategory] = useState('');
  const [feedbackRating, setFeedbackRating] = useState('');
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [feedbackRecipient, setFeedbackRecipient] = useState('admin'); // 'admin' or 'psychologist'
  const [submittingFeedback, setSubmittingFeedback] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [sessionHistory, setSessionHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [questionsPage, setQuestionsPage] = useState(1);
  const [questionsItemsPerPage, setQuestionsItemsPerPage] = useState(5);
  const [showNewQuestionDialog, setShowNewQuestionDialog] = useState(false);
  const [newQuestionType, setNewQuestionType] = useState('general');
  const [newQuestionSession, setNewQuestionSession] = useState('');
  const [newQuestionSubject, setNewQuestionSubject] = useState('');
  const [newQuestionMessage, setNewQuestionMessage] = useState('');
  const [submittingQuestion, setSubmittingQuestion] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [submittingReply, setSubmittingReply] = useState(false);
  const [uploadedAttachments, setUploadedAttachments] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [myFeedback, setMyFeedback] = useState([]);
  const [loadingMyFeedback, setLoadingMyFeedback] = useState(false);
  const [showFeedbackDetailModal, setShowFeedbackDetailModal] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [showDeleteFeedbackModal, setShowDeleteFeedbackModal] = useState(false);
  const [deletingFeedback, setDeletingFeedback] = useState(false);
  const [feedbackPage, setFeedbackPage] = useState(1);
  const [feedbackItemsPerPage, setFeedbackItemsPerPage] = useState(5);
  const [replyAttachments, setReplyAttachments] = useState({});
  const [viewThreadDialog, setViewThreadDialog] = useState(false);
  const [viewingThread, setViewingThread] = useState(null);
  const [showEndChatModal, setShowEndChatModal] = useState(false);
  const [endingChat, setEndingChat] = useState(false);

  const [showGridView, setShowGridView] = useState(true);
  const [consultations, setConsultations] = useState([]);
  const [loadingConsultations, setLoadingConsultations] = useState(false);
  const [assessmentValidityDays, setAssessmentValidityDays] = useState(90); // Default to 90, will be fetched from admin settings
  const [sendingConsultation, setSendingConsultation] = useState(null);
  const [consultationMessage, setConsultationMessage] = useState('');
  const [showBookingWarning, setShowBookingWarning] = useState(false);
  const [pendingBookingSessionId, setPendingBookingSessionId] = useState(null);
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [upcomingConsultation, setUpcomingConsultation] = useState(null);
  const [showRequestRescheduleModal, setShowRequestRescheduleModal] = useState(false);
  const [rescheduleRequestConsultation, setRescheduleRequestConsultation] = useState(null);
  const [rescheduleRequestReason, setRescheduleRequestReason] = useState('');
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [editQuestionSubject, setEditQuestionSubject] = useState('');
  const [editQuestionMessage, setEditQuestionMessage] = useState('');
  const [showEditQuestionDialog, setShowEditQuestionDialog] = useState(false);
  const [updatingQuestion, setUpdatingQuestion] = useState(false);
  const [psychologists, setPsychologists] = useState([]);
  const [loadingPsychologists, setLoadingPsychologists] = useState(false);
  const [selectedPsychologist, setSelectedPsychologist] = useState(null);
  const [showPsychologistDetailModal, setShowPsychologistDetailModal] = useState(false);
  const [bookingPsychologistId, setBookingPsychologistId] = useState(null);
  const [appointmentDate, setAppointmentDate] = useState('');
  const [appointmentTime, setAppointmentTime] = useState('');
  const [submittingBooking, setSubmittingBooking] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [consultationToCancel, setConsultationToCancel] = useState(null);
  const [cancellingConsultation, setCancellingConsultation] = useState(false);
  const [cancellationReason, setCancellationReason] = useState('');
  
  // Pagination states
  const [psychologistsPage, setPsychologistsPage] = useState(1);
  const [psychologistsPerPage, setPsychologistsPerPage] = useState(5);
  const [sessionsPage, setSessionsPage] = useState(1);
  const [sessionsPerPage, setSessionsPerPage] = useState(5);
  const [pastConsultationsPage, setPastConsultationsPage] = useState(1);
  const [pastConsultationsPerPage, setPastConsultationsPerPage] = useState(5);
  
  // Expired session notification states
  const [showExpiredSessionModal, setShowExpiredSessionModal] = useState(false);
  const [expiredSessionMessage, setExpiredSessionMessage] = useState('');
  const [expiredSessionCount, setExpiredSessionCount] = useState(0);
  
  // Search state
  const [psychologistSearchQuery, setPsychologistSearchQuery] = useState('');

  // Text-to-Speech state
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [currentSpeakingId, setCurrentSpeakingId] = useState(null);
  const [speechRate, setSpeechRate] = useState(1.0);

  // Video Call state
  const [selectedVideoConsultation, setSelectedVideoConsultation] = useState(null);
  const [activeVideoCall, setActiveVideoCall] = useState(null);
  const [initiatingCall, setInitiatingCall] = useState(false);
  const [activeTab, setActiveTab] = useState('sessions');
  const [showVideoConsentModal, setShowVideoConsentModal] = useState(false);
  const [pendingVideoCallConsultation, setPendingVideoCallConsultation] = useState(null);

  const [incomingCall, setIncomingCall] = useState(null); // {consultation, psychologistName}
  const [userExplicitlyAccepted, setUserExplicitlyAccepted] = useState(false); // Track explicit acceptance

  const [shownCallIds, setShownCallIds] = useState(new Set()); // Track calls we've already shown

  // Dashboard status requests are intentionally separate from SessionPhases form state.
  // The sequence numbers prevent a slower, older response replacing fresher data.
  const statusRequestSequence = useRef({ sessions: 0, consultations: 0 });
  const dashboardMounted = useRef(true);



  const navigate = useNavigate();

  useEffect(() => {
    // Check if user has given consent
    if (!user?.consent_accepted) {
      setShowConsentModal(true);
    }
    
    axios.get(`${API}/client/current-psychologist`).then(res => {
      setCurrentPsychologist(res.data.psychologist);
      if (!res.data.psychologist) { setActiveTab('psychologists'); fetchPsychologists(); }
    }).catch(() => showError('Unable to load your current psychologist. Please refresh.'));
    fetchSessions();
    fetchAnalytics();
    fetchQuestions();
    fetchMyFeedback();
    fetchConsultations();
    fetchAssessmentValidityDays();
    checkExpiredSessions(); // Check for expired sessions on mount
    checkAndCancelExpiredBookings(); // Check and cancel expired bookings
  }, []);

  // Check for upcoming scheduled consultations
  useEffect(() => {
    console.log('Consultations updated:', consultations);
    if (consultations.length > 0) {
      const scheduled = consultations.find(c => 
        c.status === 'scheduled' && c.consultation_date
      );
      console.log('Scheduled consultation found:', scheduled);
      if (scheduled) {
        const consultDate = new Date(scheduled.consultation_date);
        const now = new Date();
        const hoursDiff = (consultDate - now) / (1000 * 60 * 60);
        console.log('Hours until consultation:', hoursDiff);
        console.log('Consultation date:', consultDate);
        console.log('Now:', now);
        
        // Show reminder only once per login session
        const reminderShown = sessionStorage.getItem('appointmentReminderShown');
        
        if (scheduled && !reminderShown) {
          console.log('Showing appointment reminder (once per session)');
          setUpcomingConsultation(scheduled);
          setShowReminderModal(true);
          sessionStorage.setItem('appointmentReminderShown', 'true');
        }
      }
    }
  }, [consultations]);

  // Text-to-Speech functions
  const speakText = (text, id) => {
    window.speechSynthesis.cancel();
    
    if (currentSpeakingId === id && isSpeaking) {
      window.speechSynthesis.pause();
      setIsSpeaking(false);
      return;
    }
    
    const cleanContent = text
      .replace(/[#*_`]/g, '')
      .replace(/\n\n+/g, '. ')
      .replace(/\n/g, ' ')
      .trim();
    
    const utterance = new SpeechSynthesisUtterance(cleanContent);
    utterance.rate = speechRate;
    
    utterance.onend = () => {
      setIsSpeaking(false);
      setCurrentSpeakingId(null);
    };
    
    utterance.onerror = (event) => {
      console.error('Speech error:', event);
      showError('Failed to read text. Please try again.');
      setIsSpeaking(false);
      setCurrentSpeakingId(null);
    };
    
    window.speechSynthesis.speak(utterance);
    setIsSpeaking(true);
    setCurrentSpeakingId(id);
  };

  const stopSpeaking = () => {
    window.speechSynthesis.cancel();
    setIsSpeaking(false);
    setCurrentSpeakingId(null);
  };

  const fetchSessions = async ({ background = false } = {}) => {
    const requestId = ++statusRequestSequence.current.sessions;
    try {
      const res = await axios.get(`${API}/sessions`);
      if (dashboardMounted.current && requestId === statusRequestSequence.current.sessions) {
        setSessions(res.data);
      }
    } catch (error) {
      if (!background) showError('Failed to load sessions');
    }
  };

  const fetchAssessmentValidityDays = async () => {
    try {
      const res = await axios.get(`${API}/assessment-validity-days`);
      if (res.data && res.data.assessment_validity_days) {
        setAssessmentValidityDays(res.data.assessment_validity_days);
        console.log('✅ Analysis validity days loaded:', res.data.assessment_validity_days);
      }
    } catch (error) {
      console.error('Failed to fetch analysis validity days:', error);
      console.log('Using default analysis validity: 90 days');
      // Keep default 90 days if fetch fails
    }
  };

  const checkExpiredSessions = async () => {
    try {
      const res = await axios.get(`${API}/sessions/check-expired`);
      if (res.data && res.data.expired_sessions && res.data.expired_sessions.length > 0) {
        setExpiredSessionCount(res.data.expired_sessions.length);
        setExpiredSessionMessage(res.data.message);
        setShowExpiredSessionModal(true);
        // Refresh sessions to show updated status
        fetchSessions();
      }
    } catch (error) {
      console.error('Failed to check expired sessions:', error);
      // Silent fail - not critical for user experience
    }
  };

  const checkAndCancelExpiredBookings = async () => {
    try {
      // Trigger the backend to check and cancel expired bookings
      await axios.post(`${API}/consultations/check-and-cancel`);
      // Refresh consultations to show updated status
      fetchConsultations();
    } catch (error) {
      console.error('Failed to check expired bookings:', error);
      // Silent fail - not critical for user experience
    }
  };


  const handleStartNewAssessment = async (consultationId, psychologistId) => {
    try {
      // Create a new session for the consultation
      const sessionRes = await axios.post(`${API}/sessions`, {
        psychologist_id: psychologistId
      });
      
      const newSessionId = sessionRes.data.id;
      
      // Update the consultation to use the new session
      await axios.patch(`${API}/consultations/${consultationId}/session`, {
        session_id: newSessionId
      });
      
      // Navigate to the new session
      navigate(`/session/${newSessionId}`);
    } catch (error) {
      console.error('Failed to create new session:', error);
      alert('Failed to start new analysis. Please try again.');
    }
  };

  const fetchSessionHistory = async () => {
    setLoadingHistory(true);
    try {
      const res = await axios.get(`${API}/sessions/history`);
      setSessionHistory(res.data);
    } catch (error) {
      console.error('Failed to load session history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };



  const fetchMyFeedback = async () => {
    setLoadingMyFeedback(true);
    try {
      const res = await axios.get(`${API}/my-feedback`);
      setMyFeedback(res.data);
    } catch (error) {
      console.log('Failed to load feedback');
    } finally {
      setLoadingMyFeedback(false);
    }
  };

  const handleOpenFeedback = (feedback) => {
    setSelectedFeedback(feedback);
    setShowFeedbackDetailModal(true);
  };

  const handleDeleteFeedbackClick = (feedback) => {
    setSelectedFeedback(feedback);
    setShowDeleteFeedbackModal(true);
  };

  const handleConfirmDeleteFeedback = async () => {
    if (!selectedFeedback) return;
    
    setDeletingFeedback(true);
    try {
      await axios.delete(`${API}/my-feedback/${selectedFeedback.id}`);
      showSuccess('Feedback deleted successfully');
      setShowDeleteFeedbackModal(false);
      setSelectedFeedback(null);
      fetchMyFeedback(); // Refresh the list
    } catch (error) {
      showError(error.response?.data?.detail || 'Failed to delete feedback');
    } finally {
      setDeletingFeedback(false);
    }
  };

  const fetchAnalytics = async () => {
    setLoadingAnalytics(true);
    try {
      const res = await axios.get(`${API}/client/analytics`);
      console.log('=== CLIENT ANALYTICS RESPONSE ===');
      console.log('Full response:', res.data);
      console.log('Overview:', res.data?.overview);
      console.log('Sessions by status:', res.data?.sessions_by_status);
      console.log('Phase progress:', res.data?.phase_progress);
      console.log('Reports available:', res.data?.reports_available);
      console.log('================================');
      setAnalytics(res.data);
    } catch (error) {
      console.error('Analytics error:', error);
      showError('Failed to load analytics');
    } finally {
      setLoadingAnalytics(false);
    }
  };

  const fetchConsultations = async ({ background = false } = {}) => {
    const requestId = ++statusRequestSequence.current.consultations;
    if (!background) setLoadingConsultations(true);
    try {
      const res = await axios.get(`${API}/consultations`);
      if (dashboardMounted.current && requestId === statusRequestSequence.current.consultations) {
        setConsultations(res.data);
      }
    } catch (error) {
      console.error('Failed to load consultations:', error);
    } finally {
      if (!background && dashboardMounted.current && requestId === statusRequestSequence.current.consultations) {
        setLoadingConsultations(false);
      }
    }
  };

  // Keep only lightweight dashboard/status collections fresh. Failures are silent and
  // preserve the last valid state; the next interval/focus event retries automatically.
  useEffect(() => {
    dashboardMounted.current = true;
    if (!user?.id || !sessionStorage.getItem('token')) return undefined;

    const refreshDashboardStatus = () => {
      if (!dashboardMounted.current || !sessionStorage.getItem('token')) return;
      fetchSessions({ background: true });
      fetchConsultations({ background: true });
    };
    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') refreshDashboardStatus();
    };
    const interval = window.setInterval(refreshDashboardStatus, 12000);
    window.addEventListener('focus', refreshDashboardStatus);
    document.addEventListener('visibilitychange', refreshWhenVisible);

    return () => {
      dashboardMounted.current = false;
      window.clearInterval(interval);
      window.removeEventListener('focus', refreshDashboardStatus);
      document.removeEventListener('visibilitychange', refreshWhenVisible);
    };
  }, [user?.id]);


  // Poll for incoming video calls
  const checkForIncomingCall = async () => {
    try {
      const res = await axios.get(`${API}/consultations`);
      const activeConsultations = res.data.filter(c => c.status === 'scheduled' || c.status === 'approved');
      
      // Check if any active consultation has a new video_call_id
      for (const consultation of activeConsultations) {
        if (consultation.video_call_id && 
            !activeVideoCall && 
            !incomingCall && 
            !shownCallIds.has(consultation.video_call_id)) {
          
          // Check if this video call is still active (not ended)
          try {
            const videoCallRes = await axios.get(`${API}/video-calls/consultation/${consultation.id}`);
            const videoCall = videoCallRes.data;
            
            // CRITICAL: Only show ring modal - NEVER auto-join
            // Client must explicitly click "Accept" button to join
            if (videoCall && !videoCall.ended_at) {
              const psychologistName = consultation.psychologist_name || 'Psychologist';
              console.log(`🔔 Incoming call from ${psychologistName} - SHOWING RING MODAL ONLY (NO AUTO-JOIN)`);
              
              // This ONLY triggers the ring modal, does NOT join the call
              setIncomingCall({ consultation, psychologistName });
              setShownCallIds(prev => new Set([...prev, consultation.video_call_id]));
              break;
            } else {
              console.log(`✓ Video call already ended for consultation ${consultation.id}`);
            }
          } catch (error) {
            console.error('Error checking video call status:', error);
          }
        }
      }
    } catch (error) {
      console.error('Error checking for incoming calls:', error);
    }
  };

  // Set up polling for incoming calls
  useEffect(() => {
    if (!activeVideoCall) {
      const pollInterval = setInterval(checkForIncomingCall, 3000); // Check every 3 seconds
      return () => clearInterval(pollInterval);
    }
  }, [activeVideoCall, incomingCall, shownCallIds]);


  const fetchPsychologists = async () => {
    setLoadingPsychologists(true);
    try {
      const res = await axios.get(`${API}/psychologists/approved`);
      
      // Fetch ratings for each psychologist
      const psychologistsWithRatings = await Promise.all(
        res.data.filter(psych => !currentPsychologist || psych.id === currentPsychologist.id).map(async (psych) => {
          try {
            const ratingRes = await axios.get(`${API}/psychologists/${psych.id}/average-rating`);
            return { ...psych, ...ratingRes.data };
          } catch (error) {
            return { ...psych, average_rating: 0, total_ratings: 0 };
          }
        })
      );
      
      setPsychologists(psychologistsWithRatings);
    } catch (error) {
      showError('Failed to load psychologists');
    } finally {
      setLoadingPsychologists(false);
    }
  };


  const handleRequestConsultation = async (consultationType = 'initial', psychologistId = null) => {
    const targetPsychologistId = psychologistId || bookingPsychologistId;
    
    if (!targetPsychologistId) {
      showError('Please select a psychologist first');
      return;
    }
    
    if (!appointmentDate || !appointmentTime) {
      showError('Please select appointment date and time');
      return;
    }
    
    setSubmittingBooking(true);
    try {
      // Combine date and time into ISO format
      const appointmentDateTime = new Date(`${appointmentDate}T${appointmentTime}`).toISOString();
      
      // No session_id needed - backend will auto-create session
      await axios.post(`${API}/consultations`, {
        psychologist_id: targetPsychologistId,
        consultation_type: consultationType,
        request_message: consultationMessage,
        appointment_date: appointmentDateTime
      });
      const current = await axios.get(`${API}/client/current-psychologist`);
      setCurrentPsychologist(current.data.psychologist);
      setActiveTab('sessions');
      showSuccess('Booking request sent successfully!');
      showInfo('Complete the 4-phase qualitative thematic Analysis before your appointment to avoid auto-cancellation.');
      setConsultationMessage('');
      setAppointmentDate('');
      setAppointmentTime('');
      setShowPsychologistDetailModal(false);
      setBookingPsychologistId(null);
      await fetchConsultations();
      await fetchSessions(); // Refresh to show new session
    } catch (error) {
      showError(error.response?.data?.detail || 'Failed to send booking request');
    } finally {
      setSubmittingBooking(false);
    }
  };

  const handleCancelBookingClick = (consultation) => {
    setConsultationToCancel(consultation);
    setCancellationReason(''); // Reset reason when opening modal
    setShowCancelModal(true);
  };

  const handleConfirmCancelBooking = async () => {
    if (!consultationToCancel) return;
    
    // Validate cancellation reason
    if (!cancellationReason.trim()) {
      showError('Please provide a reason for cancellation');
      return;
    }
    
    setCancellingConsultation(true);
    try {
      await axios.delete(
        `${API}/consultations/${consultationToCancel.id}/cancel`,
        {
          headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` },
          params: { cancellation_reason: cancellationReason.trim() }
        }
      );
      showSuccess('Booking cancelled successfully');
      setShowCancelModal(false);
      setConsultationToCancel(null);
      setCancellationReason('');
      fetchConsultations();
      fetchSessions();
    } catch (error) {
      console.error('Cancel error:', error);
      showError(error.response?.data?.detail || 'Failed to cancel booking. Please try again.');
    } finally {
      setCancellingConsultation(false);
    }
  };

  const handleBookAppointmentClick = (sessionId) => {
    setPendingBookingSessionId(sessionId);
    setShowBookingWarning(true);
  };

  const handleProceedBooking = () => {
    handleRequestConsultation('follow_up');
  };

  const handleRequestReschedule = (consultation) => {
    setRescheduleRequestConsultation(consultation);
    setRescheduleRequestReason('');
    setShowRequestRescheduleModal(true);
  };

  const handleSubmitRescheduleRequest = async () => {
    if (!rescheduleRequestReason.trim()) {
      showError('Please provide a reason for rescheduling');
      return;
    }

    try {
      // Send reschedule request as a question to psychologist
      const scheduledDate = new Date(rescheduleRequestConsultation.consultation_date);
      const dateStr = scheduledDate.toLocaleDateString();
      const timeStr = scheduledDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      
      const message = `🔄 RESCHEDULE REQUEST\n\nScheduled Date: ${dateStr}\nScheduled Time: ${timeStr}\n\nReason: ${rescheduleRequestReason}\n\n⚠️ Client is requesting to reschedule this consultation. Please review and respond with alternative dates and times.`;
      
      await axios.post(`${API}/client/questions`, {
        session_id: rescheduleRequestConsultation.session_id,
        question_type: 'session_specific',
        subject: `⚠️ Reschedule Request - Consultation on ${dateStr} at ${timeStr}`,
        message: message,
        attachments: []
      });

      showSuccess('Reschedule request sent to psychologist. They will contact you with alternative dates.');
      setShowRequestRescheduleModal(false);
      setRescheduleRequestConsultation(null);
      setRescheduleRequestReason('');
    } catch (error) {
      showError(error.response?.data?.detail || 'Failed to send reschedule request');
    }
  };

  // Video Call Handlers

  // Handle accepting incoming call
  const handleAcceptCall = () => {
    console.log('✅✅✅ USER EXPLICITLY CLICKED ACCEPT BUTTON - Starting video call');
    // Set the explicit acceptance flag
    setUserExplicitlyAccepted(true);
    
    if (incomingCall) {
      console.log('✅ Incoming call data exists, proceeding to start video call');
      handleStartVideoCall(incomingCall.consultation);
      setIncomingCall(null);
    } else {
      console.error('❌ No incoming call to accept - this should not happen');
      setUserExplicitlyAccepted(false);
    }
  };

  // Handle declining incoming call
  const handleDeclineCall = () => {
    console.log('❌ User declined call');
    // Reset acceptance flag
    setUserExplicitlyAccepted(false);
    
    if (incomingCall?.consultation?.video_call_id) {
      // Mark this call as shown so it doesn't ring again
      setShownCallIds(prev => new Set([...prev, incomingCall.consultation.video_call_id]));
    }
    setIncomingCall(null);
    showSuccess('Call declined');
  };


  const handleStartVideoCall = async (consultation) => {
    console.log('🎥 handleStartVideoCall called for consultation:', consultation?.id);
    
    // SAFETY CHECK: Prevent automatic video call start
    if (!consultation) {
      console.error('❌ No consultation provided to start video call');
      return;
    }
    
    try {
      // Check if user has given video call consent
      if (!user?.video_consent_accepted) {
        console.log('⚠️ User needs to accept video consent first');
        // Show consent modal first
        setPendingVideoCallConsultation(consultation);
        setShowVideoConsentModal(true);
        return;
      }

      console.log('✅ User has video consent, proceeding with call');
      // User has already consented, proceed with call
      startVideoCallDirectly(consultation);
    } catch (err) {
      console.error('Error starting video call:', err);
      const errorMsg = err?.response?.data?.detail || err?.message || 'Failed to start video call';
      showError(String(errorMsg));
    }
  };

  const startVideoCallDirectly = async (consultation) => {
    try {
      console.log('🎥 startVideoCallDirectly called - CLIENT IS JOINING VIDEO CALL');
      console.log('🎥 Consultation ID:', consultation?.id);
      console.log('🎥 Video Call ID:', consultation?.video_call_id);
      console.log('🎥 Room Name:', consultation?.video_call_room);
      console.log('🎥 User explicitly accepted:', userExplicitlyAccepted);
      
      // CRITICAL SAFETY CHECK: User must have explicitly accepted
      if (!userExplicitlyAccepted) {
        console.error('❌❌❌ BLOCKED: User did not explicitly accept the call!');
        showError('Please accept the incoming call first');
        return;
      }
      
      // ADDITIONAL SAFETY: Verify the video call is still active
      try {
        const videoCallRes = await axios.get(`${API}/video-calls/consultation/${consultation.id}`);
        const videoCallStatus = videoCallRes.data;
        
        if (!videoCallStatus || videoCallStatus.ended_at) {
          console.error('❌ Video call has already ended');
          showError('This video call has already ended');
          setUserExplicitlyAccepted(false);
          return;
        }
        
        console.log('✅ Video call is active and valid');
      } catch (error) {
        console.error('❌ Failed to verify video call status:', error);
        showError('Failed to verify video call status');
        setUserExplicitlyAccepted(false);
        return;
      }
      
      setSelectedVideoConsultation(consultation);
      
      // Client joins existing video call (psychologist already started)
      if (consultation?.video_call_id && consultation?.video_call_room) {
        console.log('✅ Valid video call room found, setting activeVideoCall state');
        
        // Join existing call directly
        setActiveVideoCall({
          id: String(consultation.video_call_id),
          room_name: String(consultation.video_call_room),
          consultation_id: String(consultation.id),
          session_id: consultation.session_id ? String(consultation.session_id) : null
        });
        
        console.log('✅ Switching to video-call tab');
        // Auto-switch to video call tab
        setActiveTab('video-call');
        showSuccess('Joining video call... Switching to Live Call tab.');
        
        // Reset the acceptance flag
        setUserExplicitlyAccepted(false);
      } else {
        console.error('❌ Video call room not ready');
        showError('Psychologist has not started the video call yet. Please wait.');
        setUserExplicitlyAccepted(false);
      }
    } catch (err) {
      console.error('❌ Error joining video call:', err);
      const errorMsg = err?.response?.data?.detail || err?.message || 'Failed to join video call';
      showError(String(errorMsg));
      setUserExplicitlyAccepted(false);
    }
  };

  const handleVideoConsentAccept = async () => {
    try {
      // Save consent to backend
      await axios.patch(`${API}/users/video-call-consent`);
      
      // Update local user state
      const updatedUser = { ...user, video_consent_accepted: true };
      sessionStorage.setItem('user', JSON.stringify(updatedUser));
      
      showSuccess('Video call consent saved successfully');
      setShowVideoConsentModal(false);
      
      // Now proceed with the video call
      if (pendingVideoCallConsultation) {
        startVideoCallDirectly(pendingVideoCallConsultation);
        setPendingVideoCallConsultation(null);
      }
    } catch (error) {
      console.error('Error saving video consent:', error);
      showError('Failed to save consent. Please try again.');
    }
  };

  const handleVideoConsentDecline = () => {
    setShowVideoConsentModal(false);
    setPendingVideoCallConsultation(null);
    showSuccess('You can use alternative consultation methods (phone/in-person)');
  };

  const handleCallEnd = async (remoteEnded = false) => {
    console.log('📞 ClientDashboard: handleCallEnd called, remoteEnded:', remoteEnded);
    
    if (activeVideoCall && !remoteEnded) {
      // Only call the backend and show success if we ended the call
      try {
        await axios.post(`${API}/video-calls/${activeVideoCall.id}/end`);
        showSuccess('Video call ended successfully');
      } catch (error) {
        console.error('Error ending call:', error);
      }
    }
    
    // CRITICAL: Wait for cleanup to complete BEFORE unmounting
    // Increased to 3 seconds to ensure camera fully releases
    console.log('⏳ Waiting 3 seconds for camera cleanup to complete...');
    console.log('⏳ DO NOT REFRESH - Camera is being released...');
    await new Promise(resolve => setTimeout(resolve, 3000));
    console.log('✅ Cleanup wait complete, unmounting video call component');
    
    setActiveVideoCall(null);
    setSelectedVideoConsultation(null);
    setIncomingCall(null); // Clear any incoming call state
    setActiveTab('sessions'); // Switch back to sessions tab
    
    // Wait a bit more before refreshing to avoid race condition
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Refresh consultations to update status if needed
    fetchConsultations();
  };

  const canStartVideoCall = (consultation) => {
    if (consultation.status !== 'scheduled') return false;
    if (!consultation.consultation_date) return false;
    
    const appointmentDateTime = new Date(consultation.consultation_date);
    const now = new Date();
    const minutesUntilAppointment = (appointmentDateTime - now) / 1000 / 60;
    
    // Allow access 30 minutes before appointment
    return minutesUntilAppointment <= 30 && minutesUntilAppointment >= -60; // Allow up to 60 minutes after scheduled time
  };

  const getTimeUntilVideoCall = (consultation) => {
    if (!consultation.consultation_date) return null;
    
    const appointmentDateTime = new Date(consultation.consultation_date);
    const now = new Date();
    const minutesUntilAppointment = Math.ceil((appointmentDateTime - now) / 1000 / 60);
    
    if (minutesUntilAppointment <= 0) return 'Available now';
    if (minutesUntilAppointment <= 30) return 'Available now';
    
    const hours = Math.floor(minutesUntilAppointment / 60);
    const minutes = minutesUntilAppointment % 60;
    
    if (hours > 0) {
      return `Available in ${hours}h ${minutes}m`;
    }
    return `Available in ${minutes}m`;
  };

  const getConsultationForSession = (sessionId) => {
    return consultations.find(c => c.session_id === sessionId);
  };

  const isSystemNotification = (question) => {
    // Check if it's a system notification (cancellation, reschedule, etc.)
    const systemKeywords = ['❌ Consultation Cancelled', '📅 Consultation Rescheduled', '🔄 RESCHEDULE REQUEST'];
    return systemKeywords.some(keyword => question.subject?.includes(keyword));
  };

  const createSession = async () => {
    if (creatingSession) return; // Prevent double-click
    
    setCreatingSession(true);
    try {
      const res = await axios.post(`${API}/sessions`);
      showSuccess('New session created!');
      navigate(`/session/${res.data.id}`);
    } catch (error) {
      showError('Failed to create session');
    } finally {
      setCreatingSession(false);
    }
  };

  // Check if client can take/continue analysis - NEW LOGIC (PERMANENT)
  const canTakeNewAssessment = () => {
    // RULE 1: Check if there's a session in progress (Phase 1-4 not completed)
    const sessionInProgress = sessions.find(s => s.current_phase >= 1 && s.current_phase < 5 && s.status !== 'completed');
    if (sessionInProgress) {
      // Analysis IN PROGRESS - Button ENABLED so client can continue
      return { canTake: true, reason: 'in_progress', session: sessionInProgress };
    }
    
    // RULE 2: Find the most recent session that completed Phase 4
    const phase4CompletedSessions = sessions
      .filter(s => s.current_phase >= 5) // Phase 5 means Phase 4 is completed
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    
    if (phase4CompletedSessions.length === 0) {
      // No Phase 4 completed yet - first time user - Button ENABLED
      return { canTake: true, reason: 'ready' };
    }
    
    const lastCompletedSession = phase4CompletedSessions[0];
    
    // The backend is authoritative; this derived flag includes the global mode.
    if (lastCompletedSession.analysis_valid === true) {
      return {
        canTake: false,
        reason: 'completed_and_valid',
        daysRemaining: lastCompletedSession.assessment_days_remaining,
        expiryDate: lastCompletedSession.assessment_validity_expires_at
      };
    }
    return { canTake: true, reason: 'expired' };
  };

  const deleteSession = async (sessionId) => {
    console.log('Attempting to delete session:', sessionId);
    console.log('Auth token:', axios.defaults.headers.common["Authorization"]);
    
    try {
      const response = await axios.delete(`${API}/sessions/${sessionId}`);
      console.log('Delete response:', response.data);
      showSuccess('Session deleted successfully!');
      fetchSessions();
    } catch (error) {
      console.error('Delete error details:', error);
      console.error('Error response:', error.response);
      showError(error.response?.data?.detail || 'Failed to delete session');
    }
  };

  const getPhaseProgress = (phase) => {
    // Clients see progress out of 4, others out of 5
    const maxPhase = 4;
    const cappedPhase = phase > 4 ? 4 : phase;
    return (cappedPhase / maxPhase) * 100;
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      showError('New passwords do not match');
      return;
    }
    
    if (newPassword.length < 6) {
      showError('Password must be at least 6 characters');
      return;
    }
    
    setChangingPassword(true);
    try {
      await axios.put(`${API}/users/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      });
      showSuccess('Password changed successfully!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      showError(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleSubmitFeedback = async (e) => {
    e.preventDefault();
    
    if (!feedbackCategory) {
      showError('Please select a category');
      return;
    }
    
    if (!feedbackMessage.trim()) {
      showError('Please enter your feedback');
      return;
    }
    
    setSubmittingFeedback(true);
    try {
      await axios.post(`${API}/feedback`, {
        category: feedbackCategory,
        message: feedbackMessage,
        rating: feedbackRating ? parseInt(feedbackRating) : null,
        recipient_type: feedbackRecipient,
        recipient_id: feedbackRecipient === 'psychologist' ? 
          sessions.find(s => s.psychologist_id)?.psychologist_id : null
      });
      showSuccess('Feedback submitted successfully!');
      setShowFeedback(false);
      setFeedbackCategory('');
      setFeedbackRating('');
      setFeedbackMessage('');
      setFeedbackRecipient('admin');
      await fetchMyFeedback();
    } catch (error) {
      showError('Failed to submit feedback');
    } finally {
      setSubmittingFeedback(false);
    }
  };

  const handleEndChat = async () => {
    if (!viewingThread) return;
    
    setEndingChat(true);
    try {
      await axios.patch(`${API}/questions/${viewingThread.id}/end-chat`);
      showSuccess('Chat ended successfully');
      setShowEndChatModal(false);
      setViewThreadDialog(false);
      setViewingThread(null);
      fetchQuestions();
    } catch (error) {
      showError(error.response?.data?.detail || 'Failed to end chat');
    } finally {
      setEndingChat(false);
    }
  };


  // File Upload Function
  const handleFileUpload = async (file, isReply = false, questionId = null) => {
    if (!file) return null;
    
    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      showError('File too large. Maximum size is 10MB');
      return null;
    }
    
    setUploadingFile(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      console.log('Uploading file:', file.name, 'Size:', file.size);
      const res = await axios.post(`${API}/questions/upload-attachment`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      console.log('Upload success:', res.data);
      showSuccess(`File "${file.name}" uploaded successfully`);
      return res.data;
    } catch (error) {
      console.error('Upload error:', error);
      showError(`Failed to upload file: ${error.response?.data?.detail || error.message}`);
      return null;
    } finally {
      setUploadingFile(false);
    }
  };

  // Q&A Functions
  const fetchQuestions = async () => {
    setLoadingQuestions(true);
    try {
      const res = await axios.get(`${API}/client/questions`);
      setQuestions(res.data);
    } catch (error) {
      showError('Failed to load questions');
    } finally {
      setLoadingQuestions(false);
    }
  };

  const handleCreateQuestion = async (e) => {
    e.preventDefault();
    
    if (!newQuestionSubject.trim()) {
      showError('Please enter a subject');
      return;
    }
    
    if (!newQuestionMessage.trim()) {
      showError('Please enter your question');
      return;
    }
    
    if (newQuestionType === 'session_specific' && !newQuestionSession) {
      showError('Please select a session');
      return;
    }
    
    setSubmittingQuestion(true);
    try {
      await axios.post(`${API}/client/questions`, {
        session_id: newQuestionType === 'session_specific' ? newQuestionSession : null,
        question_type: newQuestionType,
        subject: newQuestionSubject,
        message: newQuestionMessage,
        attachments: uploadedAttachments.map(a => a.file_url)
      });
      showSuccess('Question submitted successfully!');
      setShowNewQuestionDialog(false);
      setNewQuestionType('general');
      setNewQuestionSession('');
      setNewQuestionSubject('');
      setNewQuestionMessage('');
      setUploadedAttachments([]);
      fetchQuestions();
    } catch (error) {
      showError(error.response?.data?.detail || 'Failed to submit question');
    } finally {
      setSubmittingQuestion(false);
    }
  };

  const handleReplyToQuestion = async (questionId) => {
    if (!replyMessage.trim()) {
      showError('Please enter your reply');
      return;
    }
    
    setSubmittingReply(true);
    try {
      await axios.post(`${API}/client/questions/${questionId}/reply`, {
        message: replyMessage,
        attachments: (replyAttachments[questionId] || []).map(a => a.file_url)
      });
      showSuccess('Reply sent successfully!');
      setReplyMessage('');
      setReplyAttachments(prev => ({ ...prev, [questionId]: [] }));
      fetchQuestions();
      // Update selected question
      const updatedQuestion = questions.find(q => q.id === questionId);
      if (updatedQuestion) {
        setSelectedQuestion(updatedQuestion);
      }
    } catch (error) {
      showError('Failed to send reply');
    } finally {
      setSubmittingReply(false);
    }
  };

  const handleResolveQuestion = async (questionId) => {
    try {
      await axios.patch(`${API}/client/questions/${questionId}/resolve`);
      showSuccess('Chat closed successfully');
      fetchQuestions();
      setSelectedQuestion(null);
    } catch (error) {
      showError('Failed to close chat');
    }
  };

  const downloadQaPdf = async (questionId) => {
    try {
      const response = await axios.get(`${API}/client/questions/${questionId}/download`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `my_question_${questionId.substring(0, 8)}.txt`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showSuccess('Q&A thread downloaded');
    } catch (error) {
      console.error('Download error:', error);
      // Handle blob response errors properly
      if (error.response && error.response.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const errorData = JSON.parse(text);
          showError(errorData.detail || 'Failed to download Q&A thread');
        } catch (e) {
          showError('Failed to download Q&A thread');
        }
      } else {
        showError(error.response?.data?.detail || 'Failed to download Q&A thread');
      }
    }
  };

  const deleteMyQuestion = async (questionId) => {
    try {
      await axios.delete(`${API}/client/questions/${questionId}`);
      showSuccess('Question deleted successfully');
      fetchQuestions();
    } catch (error) {
      showError('Failed to delete question');
    }
  };

  const handleEditQuestion = (question) => {
    setEditingQuestion(question);
    setEditQuestionSubject(question.subject);
    // Get the first message (initial question)
    setEditQuestionMessage(question.messages?.[0]?.message || '');
    setShowEditQuestionDialog(true);
  };

  const handleUpdateQuestion = async (e) => {
    e.preventDefault();
    if (!editQuestionSubject.trim() || !editQuestionMessage.trim()) {
      showError('Subject and message are required');
      return;
    }

    setUpdatingQuestion(true);
    try {
      await axios.patch(`${API}/client/questions/${editingQuestion.id}?subject=${encodeURIComponent(editQuestionSubject)}&message=${encodeURIComponent(editQuestionMessage)}`);
      showSuccess('Question updated successfully');
      setShowEditQuestionDialog(false);
      setEditingQuestion(null);
      setEditQuestionSubject('');
      setEditQuestionMessage('');
      fetchQuestions();
    } catch (error) {
      showError(error.response?.data?.detail || 'Failed to update question');
    } finally {
      setUpdatingQuestion(false);
    }
  };

  const downloadAttachment = async (attachmentUrl) => {
    try {
      // Validate attachment URL
      if (!attachmentUrl || typeof attachmentUrl !== 'string') {
        console.error('Invalid attachment URL:', attachmentUrl);
        showError('Invalid attachment');
        return;
      }

      const filename = attachmentUrl.split('/').pop();
      if (!filename) {
        showError('Invalid attachment filename');
        return;
      }

      console.log('Downloading attachment:', attachmentUrl);
      
      // Remove /api prefix from attachmentUrl if it exists, since API already includes it
      const cleanUrl = attachmentUrl.startsWith('/api/') ? attachmentUrl.substring(4) : attachmentUrl;
      
      const response = await axios.get(`${API}${cleanUrl}`, {
        responseType: 'blob'
      });
      
      console.log('Download response:', response.status, response.headers['content-type']);
      
      const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      showSuccess('Attachment downloaded');
    } catch (error) {
      console.error('Attachment download error:', error);
      // Handle blob response errors properly
      if (error.response && error.response.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const errorData = JSON.parse(text);
          if (error.response?.status === 404) {
            showError('Attachment not found');
          } else if (error.response?.status === 403) {
            showError('Not authorized to download attachment');
          } else {
            showError(errorData.detail || 'Failed to download attachment');
          }
        } catch (e) {
          if (error.response?.status === 404) {
            showError('Attachment not found');
          } else if (error.response?.status === 403) {
            showError('Not authorized to download attachment');
          } else {
            showError('Failed to download attachment');
          }
        }
      } else {
        if (error.response?.status === 404) {
          showError('Attachment not found');
        } else if (error.response?.status === 403) {
          showError('Not authorized to download attachment');
        } else {
          showError(error.response?.data?.detail || 'Failed to download attachment');
        }
      }
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="client-dashboard">
      <nav className="px-8 py-6 flex justify-between items-center bg-white border-b border-gray-100 shadow-sm">
        <div className="flex items-center space-x-2">
          <img src="/insight-logo.png" alt="INSIGHT Logo" className="h-16" />
        </div>
        <div className="flex items-center space-x-4">
          <span className="text-gray-600">Welcome, {user?.name}</span>
          <Dialog open={showFeedback} onOpenChange={setShowFeedback}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="feedback-btn">
                <MessageSquare className="w-4 h-4 mr-2" /> Send Feedback
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Send Feedback</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmitFeedback} className="space-y-4">
                <div>
                  <Label htmlFor="recipient">Send To *</Label>
                  <Select value={feedbackRecipient} onValueChange={setFeedbackRecipient} required>
                    <SelectTrigger data-testid="feedback-recipient">
                      <SelectValue placeholder="Select recipient..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Admin</SelectItem>
                      {sessions.some(s => s.psychologist_id) && (
                        <SelectItem value="psychologist">My Psychologist</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                  {feedbackRecipient === 'psychologist' && sessions.find(s => s.psychologist_id) && (
                    <p className="text-xs text-gray-500 mt-1">
                      Sending to: {withDoctorTitle(sessions.find(s => s.psychologist_id)?.psychologist_name || 'Your psychologist')}
                    </p>
                  )}
                </div>
                <div>
                  <Label htmlFor="category">Category *</Label>
                  <Select value={feedbackCategory} onValueChange={setFeedbackCategory} required>
                    <SelectTrigger data-testid="feedback-category">
                      <SelectValue placeholder="Select category..." />
                    </SelectTrigger>
                    <SelectContent>
                      {feedbackRecipient === 'admin' ? (
                        <>
                          <SelectItem value="Bug Report">Bug Analysis</SelectItem>
                          <SelectItem value="Feature Request">Feature Request</SelectItem>
                          <SelectItem value="General Feedback">General Feedback</SelectItem>
                        </>
                      ) : (
                        <>
                          <SelectItem value="General Feedback">General Feedback</SelectItem>
                          <SelectItem value="Session Feedback">Session Feedback</SelectItem>
                          <SelectItem value="Service Quality">Service Quality</SelectItem>
                        </>
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="rating">Rating (optional)</Label>
                  <Select value={feedbackRating} onValueChange={setFeedbackRating}>
                    <SelectTrigger data-testid="feedback-rating">
                      <SelectValue placeholder="Rate your experience..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="5">⭐⭐⭐⭐⭐ Excellent</SelectItem>
                      <SelectItem value="4">⭐⭐⭐⭐ Good</SelectItem>
                      <SelectItem value="3">⭐⭐⭐ Average</SelectItem>
                      <SelectItem value="2">⭐⭐ Poor</SelectItem>
                      <SelectItem value="1">⭐ Very Poor</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="message">Message *</Label>
                  <Textarea
                    id="message"
                    value={feedbackMessage}
                    onChange={(e) => setFeedbackMessage(e.target.value)}
                    placeholder="Share your feedback, suggestions, or describe issues..."
                    rows={5}
                    required
                    data-testid="feedback-message"
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setShowFeedback(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={submittingFeedback} data-testid="submit-feedback">
                    {submittingFeedback ? 'Submitting...' : 'Submit Feedback'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          <Button 
            variant="ghost" 
            onClick={() => navigate('/training-module')} 
            data-testid="training-module-btn"
          >
            <BookOpen className="w-4 h-4 mr-2" /> Training
          </Button>
          <Button 
            variant="ghost" 
            onClick={() => setActiveTab('account')} 
            data-testid="client-account-btn"
          >
            <Lock className="w-4 h-4 mr-2" /> Account Settings
          </Button>
          <Button variant="ghost" onClick={logout} data-testid="client-logout-btn">
            <LogOut className="w-4 h-4 mr-2" /> Logout
          </Button>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-8 py-12">
        {currentPsychologist && <p className="mb-3 text-slate-600" data-testid="current-psychologist">Current psychologist: {currentPsychologist.name}</p>}
        <h2 className="text-4xl font-bold mb-8" style={{ fontFamily: 'Cormorant Garamond, serif' }}>My Dashboard</h2>
        
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-8 mb-8">
            <TabsTrigger 
              value="sessions" 
              data-testid="tab-sessions"
            >
              <FolderOpen className="w-4 h-4 mr-2" /> My Sessions
            </TabsTrigger>
            <TabsTrigger 
              value="history" 
              data-testid="tab-history"
              onClick={fetchSessionHistory}
            >
              <Clock className="w-4 h-4 mr-2" /> History
            </TabsTrigger>
            <TabsTrigger 
              value="psychologists" 
              data-testid="tab-psychologists" 
              onClick={fetchPsychologists}
              disabled={false}
            >
              <Brain className="w-4 h-4 mr-2" /> {currentPsychologist ? 'My Psychologist' : 'Choose Psychologist'}
            </TabsTrigger>
            {activeVideoCall && (
              <TabsTrigger 
                value="video-call" 
                data-testid="tab-video-call"
                className="bg-green-100 text-green-700"
              >
                <Video className="w-4 h-4 mr-2 animate-pulse" /> Live Call
              </TabsTrigger>
            )}
            <TabsTrigger 
              value="questions" 
              data-testid="tab-questions"
              disabled={consultations.some(c => c.status === 'pending')}
            >
              <MessageSquare className="w-4 h-4 mr-2" /> Ask Psychologist
              {questions.filter(q => q.status === 'answered').length > 0 && (
                <span className="ml-2 px-2 py-0.5 text-xs bg-green-100 text-green-800 rounded-full">
                  {questions.filter(q => q.status === 'answered').length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger 
              value="feedback" 
              data-testid="tab-feedback"
            >
              <MessageCircle className="w-4 h-4 mr-2" /> My Feedback
              {myFeedback.filter(f => f.admin_reply).length > 0 && (
                <span className="ml-2 px-2 py-0.5 text-xs bg-blue-100 text-blue-800 rounded-full">
                  {myFeedback.filter(f => f.admin_reply).length}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Analytics section removed */}


          <TabsContent value="psychologists" data-testid="psychologists-content">
            {loadingPsychologists ? (
              <div className="flex justify-center items-center h-64">
                <Brain className="w-8 h-8 animate-pulse text-blue-600" />
              </div>
            ) : (
              <div className="space-y-4">
                
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-2xl font-semibold" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                    {currentPsychologist ? 'My Psychologist' : 'Choose Your Psychologist'}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {psychologists.filter(p => 
                      !psychologistSearchQuery || 
                      p.name?.toLowerCase().includes(psychologistSearchQuery.toLowerCase()) ||
                      p.email?.toLowerCase().includes(psychologistSearchQuery.toLowerCase()) ||
                      p.proforma?.specialization?.toLowerCase().includes(psychologistSearchQuery.toLowerCase())
                    ).length} psychologist{psychologists.filter(p => 
                      !psychologistSearchQuery || 
                      p.name?.toLowerCase().includes(psychologistSearchQuery.toLowerCase()) ||
                      p.email?.toLowerCase().includes(psychologistSearchQuery.toLowerCase()) ||
                      p.proforma?.specialization?.toLowerCase().includes(psychologistSearchQuery.toLowerCase())
                    ).length !== 1 ? 's' : ''} available
                  </p>
                </div>
                
                {/* Search Box */}
                <div className="mb-6">
                  <Input
                    type="text"
                    placeholder="Search by name, email, or specialization..."
                    value={psychologistSearchQuery}
                    onChange={(e) => {
                      setPsychologistSearchQuery(e.target.value);
                      setPsychologistsPage(1); // Reset to first page when searching
                    }}
                    className="max-w-md"
                  />
                </div>
                
                {psychologists.filter(p => 
                  !psychologistSearchQuery || 
                  p.name?.toLowerCase().includes(psychologistSearchQuery.toLowerCase()) ||
                  p.email?.toLowerCase().includes(psychologistSearchQuery.toLowerCase()) ||
                  p.proforma?.specialization?.toLowerCase().includes(psychologistSearchQuery.toLowerCase())
                ).length === 0 ? (
                  <Card className="p-8 text-center">
                    <Brain className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                    <p className="text-gray-600">
                      {psychologistSearchQuery ? `No psychologists found for "${psychologistSearchQuery}"` : 'No approved psychologists available at the moment.'}
                    </p>
                  </Card>
                ) : (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {psychologists
                        .filter(p => 
                          !psychologistSearchQuery || 
                          p.name?.toLowerCase().includes(psychologistSearchQuery.toLowerCase()) ||
                          p.email?.toLowerCase().includes(psychologistSearchQuery.toLowerCase()) ||
                          p.proforma?.specialization?.toLowerCase().includes(psychologistSearchQuery.toLowerCase())
                        )
                        .slice((psychologistsPage - 1) * psychologistsPerPage, psychologistsPage * psychologistsPerPage)
                        .map((psychologist) => {
                        const hasActiveBooking = consultations.some(c => 
                          c.psychologist_id === psychologist.id && ['pending', 'approved', 'scheduled', 'payment_pending'].includes(c.status)
                        );
                        
                        return (
                          <Card key={psychologist.id} className="hover:shadow-lg transition-shadow">
                            <CardHeader>
                              <div className="flex items-start gap-4">
                                {/* Profile Photo */}
                                {psychologist.proforma?.photo_url ? (
                                  <img 
                                    src={`${API}${psychologist.proforma.photo_url}`} 
                                    alt={psychologist.name}
                                    className="w-20 h-20 rounded-full object-cover border-2 border-blue-200"
                                  />
                                ) : (
                                  <div className="w-20 h-20 rounded-full bg-blue-100 flex items-center justify-center">
                                    <Brain className="w-10 h-10 text-blue-600" />
                                  </div>
                                )}
                                
                                <div className="flex-1">
                                  <CardTitle className="text-xl" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                                    {psychologist.name}
                                  </CardTitle>
                                  {psychologist.proforma?.specialization && (
                                    <p className="text-sm text-blue-600 mt-1">
                                      {psychologist.proforma.specialization}
                                    </p>
                                  )}
                                  <div className="mt-2">
                                    <StarRating 
                                      rating={psychologist.average_rating || 0} 
                                      totalRatings={psychologist.total_ratings || 0}
                                    />
                                  </div>
                                </div>
                              </div>
                            </CardHeader>
                            <CardContent className="space-y-3">
                              {psychologist.proforma?.experience_years && (
                                <div className="text-sm">
                                  <span className="font-semibold">Experience:</span> {psychologist.proforma.experience_years} years
                                </div>
                              )}
                              {psychologist.proforma?.qualifications && (
                                <div className="text-sm">
                                  <span className="font-semibold">Qualifications:</span> {psychologist.proforma.qualifications}
                                </div>
                              )}
                              
                              <p className="text-xs text-gray-600">Consultation fees are arranged directly with your psychologist outside INSIGHT.</p>
                              {psychologist.proforma?.bio && (
                                <p className="text-sm text-gray-600 line-clamp-3">
                                  {psychologist.proforma.bio}
                                </p>
                              )}
                              <Button 
                                onClick={async () => {
                                  try {
                                    const res = await axios.post(`${API}/client/current-psychologist/choose`, { psychologist_id: psychologist.id });
                                    setCurrentPsychologist(res.data.psychologist);
                                    setPsychologists([psychologist]);
                                    setSelectedPsychologist(psychologist);
                                    setShowPsychologistDetailModal(true);
                                  } catch (error) { showError(error.response?.data?.detail || 'Unable to select psychologist'); }
                                }}
                                className="w-full mt-4"
                                disabled={hasActiveBooking}
                              >
                                {hasActiveBooking ? 'Active Booking Exists' : 'View Details & Book'}
                              </Button>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                    
                    {/* Pagination Component */}
                    <Pagination
                      currentPage={psychologistsPage}
                      totalItems={psychologists.filter(p => 
                        !psychologistSearchQuery || 
                        p.name?.toLowerCase().includes(psychologistSearchQuery.toLowerCase()) ||
                        p.email?.toLowerCase().includes(psychologistSearchQuery.toLowerCase()) ||
                        p.proforma?.specialization?.toLowerCase().includes(psychologistSearchQuery.toLowerCase())
                      ).length}
                      itemsPerPage={psychologistsPerPage}
                      onPageChange={setPsychologistsPage}
                      onItemsPerPageChange={setPsychologistsPerPage}
                    />
                  </>
                )}
              </div>
            )}
          </TabsContent>

          <TabsContent value="sessions" data-testid="sessions-content">
            {/* Pending Bookings Section */}
            {consultations.filter(c => c.status === 'pending').length > 0 && (
              <div className="mb-8">
                <h3 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  Pending Bookings
                </h3>
                <div className="space-y-3">
                  {consultations.filter(c => c.status === 'pending').map((consultation) => {
                    const createdDate = new Date(consultation.created_at);
                    const hoursWaiting = Math.floor((new Date() - createdDate) / (1000 * 60 * 60));
                    
                    // Calculate expiry based on appointment time, not creation time
                    // Changed from 48 hours to 60 minutes
                    const minutesWaiting = Math.floor(hoursWaiting * 60); // Convert hours to minutes
                    let expiresInMinutes = 60 - minutesWaiting; // Expires in 60 minutes
                    let timeUntilAppointment = null;
                    
                    if (consultation.appointment_date) {
                      const appointmentDate = new Date(consultation.appointment_date);
                      const now = new Date();
                      timeUntilAppointment = Math.floor((appointmentDate - now) / (1000 * 60 * 60));
                    }
                    
                    return (
                      <Card key={consultation.id} className="border-l-4 border-blue-400">
                        <CardContent className="pt-4">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <Clock className="w-5 h-5 text-blue-500" />
                                <h4 className="font-semibold text-lg">Waiting for {consultation.psychologist_name}'s Approval</h4>
                              </div>
                              <p className="text-sm text-gray-600">
                                Appointment: {new Date(consultation.appointment_date).toLocaleString()}
                              </p>
                              <p className="text-xs text-gray-500 mt-1">
                                Requested {hoursWaiting}h ago
                              </p>
                              {consultation.reusing_existing_assessment && (
                                <div className="mt-2 p-2 bg-blue-50 rounded border border-blue-200">
                                  <p className="text-xs text-blue-700 font-medium">
                                    ✅ Using existing valid Analysis - No need to retake!
                                  </p>
                                </div>
                              )}
                              {consultation.appointment_date ? (
                                timeUntilAppointment > 0 ? (
                                  expiresInMinutes > 0 ? (
                                    <p className="text-xs text-blue-600 mt-2">
                                      ⏳ Expires in {expiresInMinutes} minutes if no response (60-min timeout) OR when appointment time passes, whichever comes first.
                                    </p>
                                  ) : (
                                    <p className="text-xs text-red-600 mt-2">
                                      ⚠️ 60-minute timeout passed! Will auto-cancel soon if psychologist doesn't respond.
                                    </p>
                                  )
                                ) : (
                                  <p className="text-xs text-red-600 mt-2">
                                    ⚠️ Appointment time has passed - will be auto-cancelled soon
                                  </p>
                                )
                              ) : (
                                expiresInMinutes > 0 ? (
                                  <p className="text-xs text-blue-600 mt-2">
                                    ⏳ Expires in {expiresInMinutes} minutes if no response. You'll be notified.
                                  </p>
                                ) : (
                                  <p className="text-xs text-red-600 mt-2">
                                    ⚠️ 60-minute timeout passed - will auto-cancel soon
                                  </p>
                                )
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            )}
            
            <div className="flex justify-between items-center mb-8">
              <h3 className="text-2xl font-semibold" style={{ fontFamily: 'Cormorant Garamond, serif' }}>My Sessions</h3>
              {/* Session creation disabled - clients must book psychologist first */}
            </div>

            {/* Show "No Sessions Yet" message when there are no active consultations */}
            {consultations.filter(c => ['pending', 'payment_pending', 'approved', 'scheduled'].includes(c.status)).length === 0 ? (
              <div className="glass-card p-12 text-center" data-testid="no-sessions-message">
                <Calendar className="w-16 h-16 mx-auto mb-4" style={{ color: '#6B9DD6' }} />
                <h3 className="text-2xl font-semibold mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>No Sessions Yet</h3>
                <p className="text-gray-600 mb-4">To start your Counselling, you need to book a psychologist first.</p>
                <div className="bg-blue-50 p-6 rounded-lg max-w-md mx-auto">
                  <h4 className="font-semibold text-blue-900 mb-3">How to Start:</h4>
                  <ol className="text-left space-y-2 text-sm text-gray-700">
                    <li>1️⃣ Go to "Choose Psychologist" tab</li>
                    <li>2️⃣ Browse and select a psychologist</li>
                    <li>3️⃣ Schedule an appointment date & time</li>
                    <li>4️⃣ Complete the 4-phase analysis prior to your appointment if you are a new patient or if your previous analysis has expired.</li>
                  </ol>
                </div>
              </div>
            ) : null}

            {/* Show "Booking Pending Approval" message when there are pending consultations */}
            {consultations.filter(c => c.status === 'pending').length > 0 && consultations.filter(c => c.status === 'payment_pending').length === 0 && (
              <div className="glass-card p-12 text-center mb-8" data-testid="pending-approval-message">
                <Clock className="w-16 h-16 mx-auto mb-4 text-blue-500" />
                <h3 className="text-2xl font-semibold mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Booking Pending Approval</h3>
                <p className="text-gray-600 mb-4">Your booking request is waiting for psychologist approval.</p>
                <div className="bg-blue-50 p-6 rounded-lg max-w-md mx-auto">
                  <h4 className="font-semibold text-blue-900 mb-3">What Happens Next:</h4>
                  <ol className="text-left space-y-2 text-sm text-gray-700">
                    <li>⏳ Psychologist will review your booking request</li>
                    <li>🔔 You'll receive a notification when approved</li>
                    <li>📝 Once approved, perform the analysis if it is the first time or if {assessmentValidityDays} days have been completed.</li>
                    <li>⚠️ <strong>Booking auto-cancels if:</strong>
                      <ul className="ml-6 mt-1 space-y-1 text-xs">
                        <li>• Psychologist doesn't respond within 60 minutes, OR</li>
                        <li>• Appointment time passes without approval</li>
                      </ul>
                    </li>
                  </ol>
                  <p className="text-xs text-blue-700 mt-4 pt-4 border-t border-blue-200">
                    💡 All tabs are disabled until psychologist approves your booking. Please check back later or wait for notification.
                  </p>
                </div>
              </div>
            )}

            {/* Scheduled Consultations with Phase Progress */}
            {consultations.filter(c => c.status === 'scheduled' || c.status === 'approved').length > 0 && (
              <div className="mb-6 space-y-4">
                {consultations.filter(c => c.status === 'scheduled' || c.status === 'approved').map((consultation) => {
                  const session = sessions.find(s => s.id === consultation.session_id);
                  
                  const { completed: completedPhases, count: phasesComplete, allComplete: allPhasesComplete } = getClientPhaseProgress(session);
                  const phase5Complete = session?.phase5_analysis_complete || false;

                  return (
                    <Card key={consultation.id} data-testid={`client-progress-${session?.id}`} className="border-2 border-blue-300 bg-blue-50">
                      <CardHeader>
                        <div className="flex items-center gap-2">
                          <CardTitle>Upcoming Consultation with {withDoctorTitle(consultation.psychologist_name)}</CardTitle>
                          <span className="px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-700">
                            {consultation.status === 'scheduled' ? '✓ Confirmed' : '⏳ Approved'}
                          </span>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {/* Appointment Date/Time */}
                        {consultation.consultation_date && (
                          <div className="bg-white p-4 rounded-lg border-2 border-blue-200">
                            <p className="text-sm font-medium text-gray-600 mb-2">📅 Scheduled Appointment</p>
                            <p className="text-lg font-bold">
                              {new Date(consultation.consultation_date).toLocaleDateString('en-US', { 
                                weekday: 'long', 
                                month: 'long', 
                                day: 'numeric', 
                                year: 'numeric' 
                              })}
                            </p>
                            <p className="text-xl font-bold text-blue-600">
                              🕐 {new Date(consultation.consultation_date).toLocaleTimeString([], { 
                                hour: '2-digit', 
                                minute: '2-digit' 
                              })}
                            </p>
                          </div>
                        )}
                        
                        {/* Analysis Validity Notice - ONLY show when Phase 5 analysis is complete AND analysis is still valid */}
                        {consultation.reusing_existing_assessment && session?.analysis_valid && session?.phase5_analysis_complete ? (
                          <div className="bg-green-50 p-4 rounded-lg border-2 border-green-300">
                            <div className="flex items-start gap-2">
                              <span className="text-2xl">✅</span>
                              <div>
                                <p className="text-sm font-bold text-green-900 mb-1">
                                  Analysis Validity Active
                                </p>
                                <p className="text-xs text-green-800 mb-2">
                                  You're using your previous analysis which is still valid. No need to complete phases again! Your psychologist can view your existing Analysis.
                                </p>
                                <p className="text-xs text-green-700 font-semibold">
                                  {session.analysis_always_valid ? '♾️ Valid indefinitely' : <>📅 Valid until: {new Date(session.assessment_validity_expires_at).toLocaleDateString('en-US', {
                                    month: 'long', 
                                    day: 'numeric', 
                                    year: 'numeric' 
                                  })}</>}
                                </p>
                                {!session.analysis_always_valid && <p className="text-xs text-gray-600 mt-1">
                                  ({session.assessment_days_remaining} days remaining)
                                </p>}
                                <Button
                                  size="sm"
                                  className="w-full mt-3 bg-green-700 hover:bg-green-800 font-semibold"
                                  onClick={() => navigate(`/session/${session.id}?viewReportsOnly=true`)}
                                >
                                  📄 View My Analysis
                                </Button>
                              </div>
                            </div>
                          </div>
                        ) : consultation.reusing_existing_assessment && session?.assessment_expired ? (
                          /* Analysis has expired - show warning */
                          <div className="bg-red-50 p-4 rounded-lg border-2 border-red-300">
                            <div className="flex items-start gap-2">
                              <span className="text-2xl">⚠️</span>
                              <div>
                                <p className="text-sm font-bold text-red-900 mb-1">
                                  Analysis Validity Expired
                                </p>
                                <p className="text-xs text-red-800 mb-2">
                                  Your previous analysis has expired. You will need to complete a new 4-phase analysis for this consultation.
                                </p>
                                <p className="text-xs text-red-700 font-semibold">
                                  📅 Expired on: {new Date(session.assessment_validity_expires_at).toLocaleDateString('en-US', { 
                                    month: 'long', 
                                    day: 'numeric', 
                                    year: 'numeric' 
                                  })}
                                </p>
                                <p className="text-xs text-gray-600 mt-2 mb-3">
                                  Please complete the analysis phases before your scheduled consultation.
                                </p>
                                <Button
                                  size="sm"
                                  className="w-full bg-red-700 hover:bg-red-800 font-semibold"
                                  onClick={() => handleStartNewAssessment(consultation.id, consultation.psychologist_id)}
                                >
                                  📝 Start New Analysis
                                </Button>
                              </div>
                            </div>
                          </div>
                        ) : consultation.reusing_existing_assessment && allPhasesComplete && session?.assessment_validity_expires_at && !session?.phase5_analysis_complete ? (
                          /* Reusing analysis but psychologist hasn't completed analysis yet */
                          <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border-2 border-blue-200">
                            <div className="flex items-center justify-between mb-3">
                              <p className="text-base font-bold text-blue-900">
                                📊 Analysis Complete - Awaiting Analysis
                              </p>
                            </div>
                            <div className="p-3 bg-amber-100 border-2 border-amber-400 rounded-lg">
                              <p className="text-sm font-bold text-amber-900 mb-1">
                                ⏳ Waiting for Psychologist
                              </p>
                              <p className="text-xs text-amber-800 mb-2">
                                You have completed all 4 phases of the analysis. Your psychologist is now analyzing your responses and preparing comprehensive Analysis.
                              </p>
                              <p className="text-xs text-amber-700 font-semibold">
                                You'll be notified once the Analysis is complete and ready to view.
                              </p>
                            </div>
                          </div>
                        ) : session ? (
                          /* Phase Progress Card - Only show if NOT reusing */
                          <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border-2 border-blue-200">
                            <div className="flex items-center justify-between mb-3">
                              <p className="text-base font-bold text-blue-900">
                                📊 Your Progress: {phasesComplete}/4 Phases Complete
                              </p>
                            </div>
                            
                            <div className="flex gap-3 mb-3">
                              {[1, 2, 3, 4].map((phaseNum) => {
                                const isComplete = completedPhases[phaseNum - 1];
                                return (
                                  <div key={phaseNum} className="flex items-center gap-1.5">
                                    <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shadow-sm ${
                                      isComplete ? 'bg-green-500 text-white' : 'bg-gray-300 text-gray-600'
                                    }`}>
                                      {isComplete ? '✓' : phaseNum}
                                    </span>
                                    <span className="text-xs font-medium text-gray-700">
                                      {phaseNum === 1 ? 'Client Contextual Demographics' : 
                                       phaseNum === 2 ? 'Client Moral Reflections' : 
                                       phaseNum === 3 ? 'Client Views' : 
                                       'Client Aspirations'}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                            
                            {!allPhasesComplete ? (
                              <div className="p-3 bg-blue-100 border border-blue-300 rounded-lg">
                                <p className="text-sm font-semibold text-blue-900 mb-1">
                                  📝 Continue Your Analysis
                                </p>
                                <p className="text-xs text-blue-800 mb-3">
                                  You have completed {phasesComplete} out of 4 phases. Please complete all phases before your consultation appointment.
                                </p>
                                <Button
                                  size="sm"
                                  className="w-full bg-blue-700 hover:bg-blue-800 font-semibold"
                                  onClick={() => navigate(`/session/${session.id}`)}
                                >
                                  <Brain className="w-4 h-4 mr-2" />
                                  Continue Session
                                </Button>
                              </div>
                            ) : !phase5Complete ? (
                              <div className="p-3 bg-amber-100 border-2 border-amber-400 rounded-lg">
                                <p className="text-sm font-bold text-amber-900 mb-1">
                                  ⏳ Waiting for Psychologist
                                </p>
                                <p className="text-xs text-amber-800">
                                  All phases complete! Your psychologist is analyzing your responses. You'll be notified when the Analysis is ready.
                                </p>
                              </div>
                            ) : (
                              <div className="p-3 bg-green-100 border-2 border-green-400 rounded-lg">
                                <p className="text-sm font-bold text-green-900 mb-1">
                                  🎉 Structured Qualitative Interpretative Summary Complete!
                                </p>
                                <p className="text-xs text-green-800 mb-3">
                                  Your psychologist has completed the Structured Qualitative Interpretative Summary. Your detailed psychological Analysis is now ready to view.
                                </p>
                                <Button
                                  size="sm"
                                  className="w-full bg-green-700 hover:bg-green-800 font-semibold"
                                  onClick={() => navigate(`/session/${session.id}?viewReportsOnly=true`)}
                                >
                                  📄 View My Analysis
                                </Button>
                              </div>
                            )}
                          </div>
                        ) : null}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}

            {/* Cancelled Consultations Section */}
            {consultations.filter(c => c.status === 'cancelled').length > 0 && (
              <div className="mb-8">
                <h3 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  Cancelled Consultations
                </h3>
                <div className="space-y-3">
                  {consultations.filter(c => c.status === 'cancelled').map((consultation) => {
                    const session = sessions.find(s => s.id === consultation.session_id);
                    return (
                      <Card key={consultation.id} className="border-l-4 border-red-400 bg-red-50">
                        <CardContent className="pt-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <h4 className="font-semibold text-lg">{withDoctorTitle(consultation.psychologist_name)}</h4>
                                <span className="px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-700">
                                  ✗ Cancelled
                                </span>
                                <span className={`px-2 py-1 rounded text-xs font-medium ${
                                  consultation.consultation_type === 'follow_up' 
                                    ? 'bg-blue-100 text-blue-700' 
                                    : 'bg-gray-100 text-gray-700'
                                }`}>
                                  {consultation.consultation_type === 'follow_up' ? '🔄 Follow-up' : '📋 Initial'}
                                </span>
                              </div>
                              <div className="space-y-1">
                                <p className="text-sm text-gray-600">
                                  📅 Appointment Date: {consultation.appointment_date 
                                    ? new Date(consultation.appointment_date).toLocaleDateString('en-US', { 
                                        weekday: 'long', 
                                        month: 'long', 
                                        day: 'numeric', 
                                        year: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit'
                                      })
                                    : 'Not scheduled'
                                  }
                                </p>
                                <p className="text-xs text-gray-500">
                                  Booked on: {new Date(consultation.created_at).toLocaleDateString()}
                                </p>
                                {session && (
                                  <p className="text-xs text-gray-500">
                                    Session ID: {session.id.substring(0, 8)}...
                                  </p>
                                )}
                                {consultation.cancellation_reason && (
                                  <div className="bg-red-100 p-3 rounded border border-red-300 text-sm text-red-900 mt-2">
                                    <p className="font-semibold mb-1">Cancellation Reason:</p>
                                    <p>{consultation.cancellation_reason}</p>
                                  </div>
                                )}
                                {consultation.cancelled_by && (
                                  <p className="text-xs text-gray-500 mt-1">
                                    Cancelled by: {consultation.cancelled_by === 'client' ? 'You' : 'Psychologist'}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Expired Sessions Section */}
            {sessions.filter(s => s.expired_at && new Date(s.expired_at) < new Date()).length > 0 && (
              <div className="mb-8">
                <h3 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  Expired Sessions
                </h3>
                <div className="space-y-3">
                  {sessions.filter(s => s.expired_at && new Date(s.expired_at) < new Date()).map((session) => {
                    const consultation = consultations.find(c => c.session_id === session.id);
                    return (
                      <Card key={session.id} className="border-l-4 border-gray-400 bg-gray-50">
                        <CardContent className="pt-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <h4 className="font-semibold text-lg">
                                  Session with {consultation?.psychologist_name || 'Psychologist'}
                                </h4>
                                <span className="px-2 py-1 rounded text-xs font-medium bg-gray-200 text-gray-700">
                                  ⏰ Expired
                                </span>
                                {session.status && (
                                  <span className="px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-600">
                                    Status: {session.status}
                                  </span>
                                )}
                              </div>
                              <div className="space-y-1">
                                <p className="text-sm text-gray-600">
                                  📅 Created: {new Date(session.created_at).toLocaleDateString('en-US', { 
                                    weekday: 'long', 
                                    month: 'long', 
                                    day: 'numeric', 
                                    year: 'numeric' 
                                  })}
                                </p>
                                <p className="text-sm text-gray-600">
                                  ⏰ Expired: {new Date(session.expired_at).toLocaleDateString('en-US', { 
                                    weekday: 'long', 
                                    month: 'long', 
                                    day: 'numeric', 
                                    year: 'numeric' 
                                  })}
                                </p>
                                <p className="text-xs text-gray-500">
                                  Phase: {session.current_phase || 0}/4 completed
                                </p>
                                <div className="bg-amber-50 p-3 rounded border border-amber-300 text-sm text-amber-900 mt-2">
                                  <p className="font-semibold mb-1">⚠️ Session Expired</p>
                                  <p className="text-xs">This session has expired. Please book a new consultation to continue.</p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Session cards hidden - Continue Session button now in consultation card */}
          </TabsContent>

          {/* Ask Psychologist Tab */}
          <TabsContent value="questions" data-testid="questions-content">
            <div className="space-y-6">
              {/* Header with New Question Button and View Toggle */}
              <div className="flex justify-between items-center">
                <h3 className="text-2xl font-bold" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  Questions to Psychologist
                </h3>
                <div className="flex space-x-2">
                  <Button
                    onClick={() => setShowGridView(!showGridView)}
                    variant="outline"
                    size="sm"
                  >
                    {showGridView ? '💬 Chat View' : '📊 Grid View'}
                  </Button>
                  <Dialog open={showNewQuestionDialog} onOpenChange={setShowNewQuestionDialog}>
                  <DialogTrigger asChild>
                    <Button data-testid="new-question-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Ask Question
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl">
                    <DialogHeader>
                      <DialogTitle>Ask Your Psychologist</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreateQuestion} className="space-y-4">
                      <div>
                        <Label>Question Type</Label>
                        <Select value={newQuestionType} onValueChange={setNewQuestionType}>
                          <SelectTrigger data-testid="question-type-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="general">General Question</SelectItem>
                            <SelectItem value="session_specific">About a Specific Session</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {newQuestionType === 'session_specific' && (
                        <div>
                          <Label>Select Session</Label>
                          <Select value={newQuestionSession} onValueChange={setNewQuestionSession}>
                            <SelectTrigger data-testid="session-select">
                              <SelectValue placeholder="Choose a session..." />
                            </SelectTrigger>
                            <SelectContent>
                              {sessions.map(session => (
                                <SelectItem key={session.id} value={session.id}>
                                  Session - Phase {session.current_phase}/5 | {session.status === 'completed' ? '✓ Completed' : session.status === 'in_progress' ? '⏳ In Progress' : 'New'} | Created: {new Date(session.created_at).toLocaleDateString()} at {new Date(session.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <p className="text-xs text-gray-500 mt-1">
                            Select which session your question is about
                          </p>
                        </div>
                      )}

                      <div>
                        <Label htmlFor="question-subject">Subject</Label>
                        <Input
                          id="question-subject"
                          value={newQuestionSubject}
                          onChange={(e) => setNewQuestionSubject(e.target.value)}
                          placeholder="Brief summary of your question"
                          required
                          data-testid="question-subject-input"
                        />
                      </div>

                      <div>
                        <Label htmlFor="question-message">Your Question</Label>
                        <Textarea
                          id="question-message"
                          value={newQuestionMessage}
                          onChange={(e) => setNewQuestionMessage(e.target.value)}
                          placeholder="Describe your question in detail..."
                          rows={6}
                          required
                          data-testid="question-message-input"
                        />
                      </div>

                      {/* File Attachments */}
                      <div>
                        <Label>Attachments (Optional)</Label>
                        <div className="text-xs text-gray-500 mb-2">You can upload images, PDFs, or documents (max 10MB)</div>
                        <Input
                          type="file"
                          onChange={async (e) => {
                            const file = e.target.files[0];
                            if (file) {
                              const uploadResult = await handleFileUpload(file);
                              if (uploadResult) {
                                setUploadedAttachments(prev => [...prev, uploadResult]);
                              }
                              // Reset input to allow selecting the same file again
                              e.target.value = '';
                            }
                          }}
                          disabled={uploadingFile}
                          className="mb-2"
                        />
                        {uploadingFile && (
                          <div className="flex items-center text-sm text-blue-600 mt-2">
                            <span>Uploading...</span>
                          </div>
                        )}
                        {uploadedAttachments.length > 0 && (
                          <div className="space-y-1 mt-3">
                            <div className="text-sm font-medium mb-1">Attached Files:</div>
                            {uploadedAttachments.map((attachment, idx) => (
                              <div key={idx} className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded text-sm">
                                <div className="flex items-center">
                                  <span className="text-green-700">📎 {attachment.filename}</span>
                                </div>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setUploadedAttachments(prev => prev.filter((_, i) => i !== idx))}
                                  className="text-red-600 hover:text-red-800"
                                >
                                  ✕ Remove
                                </Button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className="flex justify-end space-x-2">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setShowNewQuestionDialog(false)}
                        >
                          Cancel
                        </Button>
                        <Button type="submit" disabled={submittingQuestion}>
                          {submittingQuestion ? 'Submitting...' : 'Submit Question'}
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
              </div>

              {/* Questions List - Chat View or Grid View */}
              {!showGridView ? (
                loadingQuestions ? (
                  <div className="flex justify-center items-center h-64">
                    <p className="text-gray-500">Loading questions...</p>
                  </div>
                ) : questions.length === 0 ? (
                  <Card>
                    <CardContent className="text-center py-12">
                      <MessageSquare className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                      <p className="text-gray-500 mb-4">You haven't asked any questions yet</p>
                      <p className="text-sm text-gray-400">Click "Ask Question" to start a conversation with your psychologist</p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 gap-4">
                      {questions
                        .slice((questionsPage - 1) * questionsItemsPerPage, questionsPage * questionsItemsPerPage)
                        .map((question) => (
                    <Card key={question.id} className="hover:shadow-lg transition-shadow">
                      <CardHeader>
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <CardTitle className="text-lg mb-2">{question.subject}</CardTitle>
                            <div className="flex items-center space-x-4 text-sm text-gray-500">
                              <span>{question.question_type === 'session_specific' ? '📋 Session Question' : '💬 General Question'}</span>
                              <span>📅 {new Date(question.created_at).toLocaleDateString()}</span>
                              <span>🕐 {new Date(question.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                              {question.psychologist_name && (
                                <span className="text-blue-600">To: {question.psychologist_name}</span>
                              )}
                            </div>
                            {/* Session Status Information */}
                            {question.question_type === 'session_specific' && question.session_status && (
                              <div className="mt-2 flex items-center space-x-2 text-xs">
                                {question.session_status === 'cancelled' && (
                                  <span className="px-2 py-1 bg-red-100 text-red-800 font-medium rounded">
                                    ⚠️ Session Cancelled
                                  </span>
                                )}
                                {question.session_status === 'completed' && (
                                  <span className="px-2 py-1 bg-blue-100 text-blue-800 font-medium rounded">
                                    ✓ Session Completed
                                  </span>
                                )}
                                {question.session_status === 'in_progress' && (
                                  <span className="px-2 py-1 bg-green-100 text-green-800 font-medium rounded">
                                    ⏳ Session In Progress (Phase {question.session_current_phase}/5)
                                  </span>
                                )}
                                {question.session_status === 'not_found' && (
                                  <span className="px-2 py-1 bg-gray-100 text-gray-600 font-medium rounded">
                                    ⚠️ Session Not Found
                                  </span>
                                )}
                              </div>
                            )}
                            {question.session_cancellation_reason && (
                              <div className="mt-1 text-xs text-red-600">
                                Reason: {question.session_cancellation_reason}
                              </div>
                            )}
                          </div>
                          <div className="flex items-center space-x-2">
                            {question.status === 'open' && (
                              <span className="px-3 py-1 bg-yellow-100 text-yellow-800 text-xs font-medium rounded-full">
                                Waiting
                              </span>
                            )}
                            {question.status === 'answered' && (
                              <span className="px-3 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
                                Answered
                              </span>
                            )}
                            {question.status === 'resolved' && (
                              <span className="px-3 py-1 bg-gray-100 text-gray-800 text-xs font-medium rounded-full">
                                Resolved
                              </span>
                            )}
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-4">
                          {/* Messages Thread */}
                          <div className="space-y-3 max-h-96 overflow-y-auto">
                            {question.messages && Array.isArray(question.messages) && question.messages.map((msg, idx) => (
                              <div
                                key={idx}
                                className={`p-4 rounded-lg ${
                                  msg.sender_role === 'client'
                                    ? 'bg-blue-50 ml-8'
                                    : 'bg-green-50 mr-8'
                                }`}
                              >
                                <div className="flex justify-between items-start mb-2">
                                  <span className="font-semibold text-sm">
                                    {msg.sender_role === 'client' ? 'You' : msg.sender_name}
                                  </span>
                                  <span className="text-xs text-gray-500">
                                    {new Date(msg.timestamp).toLocaleDateString()} at {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                  </span>
                                </div>
                                <p className="text-gray-700 whitespace-pre-wrap">{msg.message}</p>
                                {/* Display Attachments */}
                                {msg.attachments && Array.isArray(msg.attachments) && msg.attachments.length > 0 && (
                                  <div className="mt-2 space-y-1">
                                    {msg.attachments.filter(att => att && typeof att === 'string' && att.trim() !== '').map((attachment, attIdx) => (
                                      <button
                                        key={attIdx}
                                        onClick={() => downloadAttachment(attachment)}
                                        className="flex items-center space-x-2 text-blue-600 hover:text-blue-800 text-sm cursor-pointer underline"
                                      >
                                        <span>📎 View Attachment</span>
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>

                          {/* Reply Section */}
                          {question.status !== 'resolved' && (
                            <div className="border-t pt-4">
                              <Textarea
                                value={selectedQuestion?.id === question.id ? replyMessage : ''}
                                onChange={(e) => {
                                  setReplyMessage(e.target.value);
                                  setSelectedQuestion(question);
                                }}
                                placeholder="Type your reply..."
                                rows={3}
                                className="mb-2"
                              />
                              {/* Reply Attachments */}
                              <div className="mb-2">
                                <Label className="text-sm mb-1 block">Attach File (Optional)</Label>
                                <Input
                                  type="file"
                                  onChange={async (e) => {
                                    const file = e.target.files[0];
                                    if (file) {
                                      const uploadResult = await handleFileUpload(file, true, question.id);
                                      if (uploadResult) {
                                        setReplyAttachments(prev => ({
                                          ...prev,
                                          [question.id]: [...(prev[question.id] || []), uploadResult]
                                        }));
                                      }
                                    }
                                    e.target.value = '';
                                  }}
                                  disabled={uploadingFile}
                                  className="text-sm mb-2"
                                />
                                {uploadingFile && (
                                  <div className="text-sm text-blue-600 mb-2">Uploading...</div>
                                )}
                                {(replyAttachments[question.id] || []).length > 0 && (
                                  <div className="mt-2 space-y-1">
                                    <div className="text-xs font-medium mb-1">Attached:</div>
                                    {replyAttachments[question.id].map((attachment, idx) => (
                                      <div key={idx} className="flex items-center justify-between p-2 bg-green-50 border border-green-200 rounded text-sm">
                                        <span className="text-green-700">📎 {attachment.filename}</span>
                                        <Button
                                          type="button"
                                          variant="ghost"
                                          size="sm"
                                          onClick={() => {
                                            setReplyAttachments(prev => ({
                                              ...prev,
                                              [question.id]: prev[question.id].filter((_, i) => i !== idx)
                                            }));
                                          }}
                                          className="text-red-600 hover:text-red-800"
                                        >
                                          ✕
                                        </Button>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                              <div className="flex justify-between items-center">
                                <div className="flex space-x-2">
                                  <Button
                                    onClick={() => handleReplyToQuestion(question.id)}
                                    disabled={submittingReply || !replyMessage.trim() || selectedQuestion?.id !== question.id}
                                    size="sm"
                                  >
                                    Send Reply
                                  </Button>
                                </div>
                                <Button
                                  onClick={() => handleResolveQuestion(question.id)}
                                  variant="destructive"
                                  size="sm"
                                  className="bg-red-600 hover:bg-red-700"
                                >
                                  🔒 Close/End Chat
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
                
                <Pagination
                  currentPage={questionsPage}
                  totalItems={questions.length}
                  itemsPerPage={questionsItemsPerPage}
                  onPageChange={setQuestionsPage}
                  onItemsPerPageChange={setQuestionsItemsPerPage}
                />
              </div>
            ) // End of accordion view
          ) : ( // Start of grid view
                <Card>
                  <CardContent className="pt-6">
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse border border-gray-300">
                        <thead className="bg-blue-50">
                          <tr>
                            <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-700">Subject</th>
                            <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-700">Psychologist</th>
                            <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-700">Type</th>
                            <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-700">Q&A Status</th>
                            <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-700">Messages</th>
                            <th className="border border-gray-300 px-4 py-3 text-center font-semibold text-gray-700">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {questions
                            .slice((questionsPage - 1) * questionsItemsPerPage, questionsPage * questionsItemsPerPage)
                            .map((question) => (
                            <tr key={question.id} className="hover:bg-gray-50">
                              <td className="border border-gray-300 px-4 py-3 text-sm">
                                <p className="font-semibold">{question.subject}</p>
                                <p className="text-xs text-gray-500">
                                  📅 {new Date(question.created_at).toLocaleDateString()} at {new Date(question.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </p>
                                {question.session_cancellation_reason && (
                                  <p className="text-xs text-red-600 mt-1">
                                    Reason: {question.session_cancellation_reason}
                                  </p>
                                )}
                              </td>
                              <td className="border border-gray-300 px-4 py-3 text-sm">
                                {question.psychologist_name || 'Not assigned'}
                              </td>
                              <td className="border border-gray-300 px-4 py-3 text-sm">
                                {question.question_type === 'session_specific' ? '📋 Session' : '💬 General'}
                              </td>
                              <td className="border border-gray-300 px-4 py-3 text-sm">
                                {question.status === 'open' && (
                                  <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-medium rounded-full">
                                    Open
                                  </span>
                                )}
                                {question.status === 'answered' && (
                                  <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
                                    Answered
                                  </span>
                                )}
                                {question.status === 'resolved' && (
                                  <span className="px-2 py-1 bg-gray-100 text-gray-800 text-xs font-medium rounded-full">
                                    Resolved
                                  </span>
                                )}
                              </td>
                              <td className="border border-gray-300 px-4 py-3 text-sm text-center">
                                <span className="font-medium">{question.messages?.length || 0}</span>
                              </td>
                              <td className="border border-gray-300 px-4 py-3">
                                <div className="flex justify-center space-x-2">
                                  <Button
                                    onClick={() => {
                                      setViewingThread(question);
                                      setViewThreadDialog(true);
                                    }}
                                    size="sm"
                                    variant="outline"
                                    title="View Full Thread"
                                  >
                                    👁️ View
                                  </Button>
                                  {question.status !== 'resolved' && !isSystemNotification(question) && (
                                    <Button
                                      onClick={() => handleEditQuestion(question)}
                                      size="sm"
                                      variant="outline"
                                      title="Edit Question"
                                      className="text-blue-600 hover:text-blue-800"
                                    >
                                      ✏️ Edit
                                    </Button>
                                  )}
                                  <Button
                                    onClick={() => downloadQaPdf(question.id)}
                                    size="sm"
                                    variant="outline"
                                    title="Download"
                                  >
                                    📄
                                  </Button>
                                  {question.status !== 'resolved' && !isSystemNotification(question) && (
                                    <Button
                                      onClick={() => handleResolveQuestion(question.id)}
                                      size="sm"
                                      className="bg-blue-500 hover:bg-blue-600 text-white"
                                      title="Close Chat"
                                    >
                                      🔒
                                    </Button>
                                  )}
                                  <Button
                                    onClick={() => deleteMyQuestion(question.id)}
                                    size="sm"
                                    variant="destructive"
                                    title="Delete"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    
                    <Pagination
                      currentPage={questionsPage}
                      totalItems={questions.length}
                      itemsPerPage={questionsItemsPerPage}
                      onPageChange={setQuestionsPage}
                      onItemsPerPageChange={setQuestionsItemsPerPage}
                    />
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Live Video Call Tab Content */}
          {activeVideoCall && (
            <TabsContent value="video-call" data-testid="video-call-content">
              <div className="w-full h-[600px] bg-white rounded-lg overflow-hidden border shadow-lg">
                <WebRTCVideoCall
                  roomName={activeVideoCall.room_name}
                  userDisplayName={user?.name || 'Client'}
                  userEmail={user?.email}
                  onCallEnd={handleCallEnd}
                  consultationId={activeVideoCall.consultation_id}
                  videoCallId={activeVideoCall.id}
                  isInitiator={false}
                />
              </div>
            </TabsContent>
          )}

          <TabsContent value="account" data-testid="account-content">
            <div className="space-y-6">
              {/* Change Password Card */}
              <Card>
                <CardHeader>
                  <CardTitle style={{ fontFamily: 'Cormorant Garamond, serif' }}>Change Password</CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleChangePassword} className="space-y-4 max-w-md">
                    <div>
                      <Label htmlFor="current-password">Current Password</Label>
                      <Input
                        id="current-password"
                        type="password"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        required
                        data-testid="current-password-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="new-password">New Password</Label>
                      <Input
                        id="new-password"
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        required
                        minLength={6}
                        data-testid="new-password-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="confirm-password">Confirm New Password</Label>
                      <Input
                        id="confirm-password"
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        minLength={6}
                        data-testid="confirm-password-input"
                      />
                    </div>
                    <Button 
                      type="submit" 
                      disabled={changingPassword}
                      data-testid="change-password-btn"
                      style={{ background: '#2563EB' }}
                    >
                      {changingPassword ? 'Changing Password...' : 'Change Password'}
                    </Button>
                  </form>
                </CardContent>
              </Card>
              
              {/* Account Settings Component */}
              <AccountSettings 
                onLogout={logout} 
                showNotification={(type, message) => {
                  if (type === 'success') showSuccess(message);
                  else if (type === 'error') showError(message);
                  else if (type === 'info') showInfo(message);
                }}
              />
            </div>
          </TabsContent>
          
          <TabsContent value="feedback" data-testid="feedback-content">
            <Card>
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Cormorant Garamond, serif' }}>My Feedback & Admin Replies</CardTitle>
              </CardHeader>
              <CardContent>
                {loadingMyFeedback ? (
                  <div className="flex justify-center items-center h-32">
                    <p className="text-gray-500">Loading feedback...</p>
                  </div>
                ) : myFeedback.length === 0 ? (
                  <div className="text-center py-8">
                    <MessageCircle className="w-12 h-12 mx-auto text-gray-400 mb-3" />
                    <p className="text-gray-600">No feedback submitted yet</p>
                    <p className="text-sm text-gray-500 mt-2">Submit feedback using the button in the top navigation</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="bg-gray-50">
                          <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-700">Name</th>
                          <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-700">Role</th>
                          <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-700">Category</th>
                          <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-700">Message Sent</th>
                          <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-700">Admin Reply</th>
                          <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-700">Date</th>
                          <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-700">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          const startIndex = (feedbackPage - 1) * feedbackItemsPerPage;
                          const endIndex = startIndex + feedbackItemsPerPage;
                          const paginatedFeedback = myFeedback.slice(startIndex, endIndex);
                          
                          return paginatedFeedback.map((feedback, idx) => (
                          <tr key={feedback.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                            <td className="border border-gray-300 px-4 py-3">{feedback.user_name}</td>
                            <td className="border border-gray-300 px-4 py-3">
                              <span className="inline-block px-2 py-1 text-xs font-medium rounded" 
                                    style={{ backgroundColor: '#E3F2FD', color: '#1976D2' }}>
                                {feedback.user_role}
                              </span>
                            </td>
                            <td className="border border-gray-300 px-4 py-3">
                              <span className="text-sm">{feedback.category}</span>
                            </td>
                            <td className="border border-gray-300 px-4 py-3">
                              <div className="max-w-xs">
                                <p className="text-sm text-gray-800 whitespace-pre-wrap">{feedback.message}</p>
                                {feedback.rating && (
                                  <p className="text-xs text-yellow-600 mt-1">Rating: {'⭐'.repeat(feedback.rating)}</p>
                                )}
                              </div>
                            </td>
                            <td className="border border-gray-300 px-4 py-3">
                              {feedback.admin_reply ? (
                                <div className="max-w-xs">
                                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                    <p className="text-sm text-blue-900 whitespace-pre-wrap">{feedback.admin_reply}</p>
                                    <p className="text-xs text-blue-600 mt-2">
                                      Replied: {new Date(feedback.admin_reply_at).toLocaleDateString()}
                                    </p>
                                  </div>
                                </div>
                              ) : (
                                <span className="text-sm text-gray-500 italic">No reply yet</span>
                              )}
                            </td>
                            <td className="border border-gray-300 px-4 py-3">
                              <span className="text-sm text-gray-600">
                                {new Date(feedback.created_at).toLocaleDateString()}
                              </span>
                            </td>
                            <td className="border border-gray-300 px-4 py-3">
                              <div className="flex gap-2">
                                <Button 
                                  variant="outline" 
                                  size="sm" 
                                  onClick={() => handleOpenFeedback(feedback)}
                                  title="View details"
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                                <Button 
                                  variant="destructive" 
                                  size="sm" 
                                  onClick={() => handleDeleteFeedbackClick(feedback)}
                                  title="Delete feedback"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                          ));
                        })()}
                      </tbody>
                    </table>
                    
                    <Pagination
                      currentPage={feedbackPage}
                      totalItems={myFeedback.length}
                      itemsPerPage={feedbackItemsPerPage}
                      onPageChange={setFeedbackPage}
                      onItemsPerPageChange={setFeedbackItemsPerPage}
                    />
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>


          {/* Session History Tab */}
          <TabsContent value="history" data-testid="history-content">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="w-6 h-6" />
                  Session History
                </CardTitle>
                <p className="text-sm text-gray-600 mt-2">
                  View all your completed sessions and past AI Analysis
                </p>
              </CardHeader>
              <CardContent>
                {loadingHistory ? (
                  <div className="text-center py-8">
                    <Loader2 className="w-8 h-8 animate-spin mx-auto text-blue-600" />
                    <p className="text-gray-600 mt-2">Loading session history...</p>
                  </div>
                ) : sessionHistory.filter(session => session.has_ai_reports || session.has_synthesis).length === 0 ? (
                  <div className="text-center py-12">
                    <Clock className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-600">No completed sessions with Analysis yet</p>
                    <p className="text-sm text-gray-500 mt-2">
                      Your past sessions and Analysis will appear here once the psychologist shares them
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {sessionHistory.filter(session => session.has_ai_reports || session.has_synthesis).map((session) => (
                      <div
                        key={session.id}
                        className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h3 className="font-semibold text-lg">
                                Session with {session.psychologist_name || 'Psychologist'}
                              </h3>
                              <span className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded">
                                Completed
                              </span>
                            </div>
                            <div className="text-sm text-gray-600 space-y-1">
                              <p>📅 Created: {new Date(session.created_at).toLocaleDateString('en-US', { 
                                month: 'long', 
                                day: 'numeric', 
                                year: 'numeric' 
                              })}</p>
                              {session.assessment_validity_expires_at && (
                                <p>{session.analysis_always_valid ? '♾️ Valid indefinitely' : <>⏰ Valid until: {new Date(session.assessment_validity_expires_at).toLocaleDateString('en-US', {
                                  month: 'long', 
                                  day: 'numeric', 
                                  year: 'numeric' 
                                })}</>} {session.assessment_expired && (
                                  <span className="text-red-600 font-semibold">(Expired)</span>
                                )}</p>
                              )}
                              {session.consultation_info?.completed_at && (
                                <p>✅ Consultation completed: {new Date(session.consultation_info.completed_at).toLocaleDateString('en-US', { 
                                  month: 'long', 
                                  day: 'numeric', 
                                  year: 'numeric' 
                                })}</p>
                              )}
                            </div>
                          </div>
                          <div className="flex flex-col gap-2">
                            {session.has_ai_reports && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => navigate(`/session/${session.id}?viewReportsOnly=true`)}
                                className="whitespace-nowrap"
                              >
                                <BookOpen className="w-4 h-4 mr-2" />
                                View Analysis
                              </Button>
                            )}
                            {!session.has_ai_reports && (
                              <span className="text-xs text-gray-500 italic">
                                No Analysis available
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-4 pt-3 border-t text-xs text-gray-500">
                          <span className="flex items-center gap-1">
                            {session.has_synthesis ? (
                              <>
                                <CheckCircle className="w-3 h-3 text-green-600" />
                                14 Points SQIS Available
                              </>
                            ) : (
                              <>
                                <XCircle className="w-3 h-3 text-gray-400" />
                                No Synthesis
                              </>
                            )}
                          </span>
                          <span className="flex items-center gap-1">
                            {session.has_ai_reports ? (
                              <>
                                <CheckCircle className="w-3 h-3 text-green-600" />
                                AI Analysis Available
                              </>
                            ) : (
                              <>
                                <XCircle className="w-3 h-3 text-gray-400" />
                                No AI Analysis
                              </>
                            )}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

        </Tabs>

        {/* View Full Thread Dialog */}
        <Dialog open={viewThreadDialog} onOpenChange={setViewThreadDialog}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <div className="flex items-center justify-between">
                <DialogTitle>{viewingThread?.subject}</DialogTitle>
                {viewingThread?.status !== 'closed' && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowEndChatModal(true)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    End Chat
                  </Button>
                )}
              </div>
            </DialogHeader>
            {viewingThread && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded">
                  <div>
                    <p className="text-sm text-gray-600">Type:</p>
                    <p className="font-medium">{viewingThread.question_type === 'session_specific' ? 'Session Question' : 'General Question'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Status:</p>
                    <p className="font-medium capitalize">{viewingThread.status}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Psychologist:</p>
                    <p className="font-medium">{viewingThread.psychologist_name || 'Not assigned'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Created:</p>
                    <p className="font-medium">
                      {new Date(viewingThread.created_at).toLocaleDateString()} at {new Date(viewingThread.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>

                <div className="space-y-3">
                  <h4 className="font-semibold">Conversation:</h4>
                  {viewingThread.messages?.map((msg, idx) => (
                    <div
                      key={idx}
                      className={`p-4 rounded-lg ${
                        msg.sender_role === 'client'
                          ? 'bg-blue-50 ml-8'
                          : 'bg-green-50 mr-8'
                      }`}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <span className="font-semibold text-sm">
                          {msg.sender_role === 'client' ? 'You' : msg.sender_name}
                        </span>
                        <span className="text-xs text-gray-500">
                          {new Date(msg.timestamp).toLocaleDateString()} at {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                      <p className="text-gray-700 whitespace-pre-wrap">{msg.message}</p>
                      {msg.attachments && Array.isArray(msg.attachments) && msg.attachments.length > 0 && (
                        <div className="mt-2 space-y-1">
                          {msg.attachments.filter(att => att && typeof att === 'string' && att.trim() !== '').map((attachment, attIdx) => (
                            <button
                              key={attIdx}
                              onClick={() => downloadAttachment(attachment)}
                              className="flex items-center space-x-2 text-blue-600 hover:text-blue-800 text-sm cursor-pointer underline"
                            >
                              <span>📎 View Attachment</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
        
        {/* End Chat Confirmation Modal */}
        <Dialog open={showEndChatModal} onOpenChange={setShowEndChatModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>End Chat?</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <p className="text-gray-700">
                Are you sure you want to end this chat? No more messages can be sent after ending the chat.
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowEndChatModal(false)}>
                Cancel
              </Button>
              <Button 
                variant="destructive" 
                onClick={handleEndChat} 
                disabled={endingChat}
                className="bg-red-600 hover:bg-red-700"
              >
                {endingChat ? 'Ending...' : 'End Chat'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    
      
      <NotificationModal
        isOpen={notification.isOpen}
        onClose={closeNotification}
        type={notification.type}
        title={notification.title}
        message={notification.message}
      />

      {/* Feedback Detail Modal */}
      <Dialog open={showFeedbackDetailModal} onOpenChange={setShowFeedbackDetailModal}>
        <DialogContent className="max-w-[700px]">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Cormorant Garamond, serif' }}>Feedback Details</DialogTitle>
          </DialogHeader>
          
          {selectedFeedback && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded">
                <div>
                  <Label className="text-sm text-gray-600">Name</Label>
                  <p className="font-medium">{selectedFeedback.user_name}</p>
                </div>
                <div>
                  <Label className="text-sm text-gray-600">Role</Label>
                  <p className="font-medium capitalize">{selectedFeedback.user_role}</p>
                </div>
                <div>
                  <Label className="text-sm text-gray-600">Category</Label>
                  <p className="font-medium">{selectedFeedback.category}</p>
                </div>
                <div>
                  <Label className="text-sm text-gray-600">Date</Label>
                  <p className="font-medium">{new Date(selectedFeedback.created_at).toLocaleDateString()}</p>
                </div>
                {selectedFeedback.rating && (
                  <div>
                    <Label className="text-sm text-gray-600">Rating</Label>
                    <p className="font-medium">{'⭐'.repeat(selectedFeedback.rating)}</p>
                  </div>
                )}
              </div>
              
              <div>
                <Label className="text-sm text-gray-600">Your Message</Label>
                <div className="mt-2 p-4 bg-white border border-gray-300 rounded-lg">
                  <p className="text-sm whitespace-pre-wrap">{selectedFeedback.message}</p>
                </div>
              </div>
              
              {selectedFeedback.admin_reply ? (
                <div>
                  <Label className="text-sm text-gray-600">Admin Reply</Label>
                  <div className="mt-2 p-4 bg-blue-50 border border-blue-300 rounded-lg">
                    <p className="text-sm text-blue-900 whitespace-pre-wrap">{selectedFeedback.admin_reply}</p>
                    <p className="text-xs text-blue-600 mt-2">
                      Replied on: {new Date(selectedFeedback.admin_reply_at).toLocaleString()}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="p-4 bg-yellow-50 border border-yellow-300 rounded-lg">
                  <p className="text-sm text-yellow-800">Admin has not replied to this feedback yet.</p>
                </div>
              )}
              
              <div className="flex justify-end">
                <Button onClick={() => setShowFeedbackDetailModal(false)} style={{ background: '#2563EB' }}>
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteFeedbackModal} onOpenChange={setShowDeleteFeedbackModal}>
        <DialogContent className="max-w-[450px]">
          <DialogHeader>
            <DialogTitle className="text-red-600" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Delete Feedback
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-sm text-gray-700 mb-4">
              Are you sure you want to delete this feedback? This action cannot be undone.
            </p>
            
            {selectedFeedback && (
              <div className="p-3 bg-gray-50 border border-gray-300 rounded-lg">
                <p className="text-xs text-gray-600 mb-1">Category: {selectedFeedback.category}</p>
                <p className="text-sm text-gray-800 line-clamp-3">{selectedFeedback.message}</p>
              </div>
            )}
          </div>
          
          <div className="flex justify-end gap-2">
            <Button 
              variant="outline" 
              onClick={() => setShowDeleteFeedbackModal(false)}
              disabled={deletingFeedback}
            >
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleConfirmDeleteFeedback}
              disabled={deletingFeedback}
            >
              {deletingFeedback ? 'Deleting...' : 'Delete'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Client Booking Warning Modal */}
      <Dialog open={showBookingWarning} onOpenChange={setShowBookingWarning}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-blue-600 flex items-center gap-2">
              <span className="text-2xl">⚠️</span> Before Booking Your Appointment
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm font-semibold text-gray-800">Important Information:</p>
            
            <div className="space-y-3 text-sm text-gray-700">
              <div className="flex gap-2">
                <span className="font-semibold min-w-fit">• Confidentiality:</span>
                <span>Your session information is kept confidential, except where required by law (risk of harm to self/others, child abuse, court orders)</span>
              </div>
              
              <div className="flex gap-2">
                <span className="font-semibold min-w-fit">• Commitment:</span>
                <span>Please attend scheduled sessions or cancel 24 hours in advance</span>
              </div>
              
              <div className="flex gap-2">
                <span className="font-semibold min-w-fit">• Emergency:</span>
                <span>If you're experiencing a mental health emergency, contact emergency services (911) or crisis hotline immediately - do not wait for your appointment</span>
              </div>
              
              <div className="flex gap-2">
                <span className="font-semibold min-w-fit">• Session Duration:</span>
                <span>Each Analysis phase takes approximately 30-60 minutes</span>
              </div>
              
              <div className="flex gap-2">
                <span className="font-semibold min-w-fit">• Professional Relationship:</span>
                <span>This is a professional therapeutic relationship maintained within appropriate boundaries</span>
              </div>
            </div>

            <div className="bg-blue-50 p-3 rounded-lg border border-blue-200 mt-4">
              <p className="text-sm text-blue-800">
                By proceeding, you acknowledge that you understand these terms.
              </p>
            </div>

            <div className="mt-4">
              <Label>Message (Optional)</Label>
              <Textarea
                value={consultationMessage}
                onChange={(e) => setConsultationMessage(e.target.value)}
                placeholder="Any specific topics you'd like to discuss..."
                rows={3}
                className="mt-1"
              />
            </div>
          </div>
          <div className="flex gap-3 justify-end">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowBookingWarning(false);
                setPendingBookingSessionId(null);
                setConsultationMessage('');
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleProceedBooking}
              disabled={sendingConsultation}
              className="bg-green-600 hover:bg-green-700"
            >
              {sendingConsultation ? 'Sending...' : 'I Understand & Proceed'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Appointment Reminder Modal */}
      {upcomingConsultation && (
        <Dialog open={showReminderModal} onOpenChange={setShowReminderModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-blue-600 flex items-center gap-2">
                <Calendar className="w-6 h-6" /> Upcoming Appointment Reminder
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <p className="text-sm font-semibold text-blue-900 mb-2">
                  You have a scheduled consultation:
                </p>
                <div className="space-y-2 text-sm text-blue-800">
                  <div className="flex justify-between">
                    <span className="font-medium">Date:</span>
                    <span className="font-bold">{new Date(upcomingConsultation.consultation_date).toLocaleDateString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium">Time:</span>
                    <span className="font-bold">{new Date(upcomingConsultation.consultation_date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium">Psychologist:</span>
                    <span>{upcomingConsultation.psychologist_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium">Status:</span>
                    <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs">Scheduled</span>
                  </div>
                </div>
              </div>

              <div className="space-y-2 text-sm text-gray-700">
                <p className="font-semibold">📋 Preparation Tips:</p>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>Review your Analysis results beforehand</li>
                  <li>Prepare any questions you'd like to discuss</li>
                  <li>Ensure you're in a quiet, private space</li>
                  <li>Have stable internet connection (if virtual)</li>
                </ul>
              </div>

              <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                <p className="text-xs text-orange-800">
                  <span className="font-semibold">⏰ Reminder:</span> Please join 5 minutes before the scheduled time. If you need to cancel or reschedule, contact your psychologist at least 24 hours in advance.
                </p>
              </div>
            </div>
            <div className="flex justify-end">
              <Button 
                onClick={() => setShowReminderModal(false)}
                className="bg-blue-600 hover:bg-blue-700"
              >
                Got It, Thanks!
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Client Reschedule Request Modal */}
      <Dialog open={showRequestRescheduleModal} onOpenChange={setShowRequestRescheduleModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-blue-600 flex items-center gap-2">
              <AlertCircle className="w-6 h-6" /> Request to Reschedule
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {rescheduleRequestConsultation && (
              <div className="bg-blue-50 p-4 rounded-lg border-2 border-blue-300">
                <p className="text-sm font-semibold text-blue-900 mb-3">📅 Consultation to Reschedule:</p>
                <div className="space-y-2 bg-white p-3 rounded border border-blue-200">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-gray-700">Date:</span>
                    <span className="text-base font-bold text-gray-900">
                      {new Date(rescheduleRequestConsultation.consultation_date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                    </span>
                  </div>
                  <div className="flex items-center justify-between border-t pt-2">
                    <span className="font-medium text-gray-700">Time:</span>
                    <span className="text-lg font-bold text-blue-700">
                      🕐 {new Date(rescheduleRequestConsultation.consultation_date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                  <div className="flex items-center justify-between border-t pt-2">
                    <span className="font-medium text-gray-700">Psychologist:</span>
                    <span className="font-semibold text-blue-700">{rescheduleRequestConsultation.psychologist_name}</span>
                  </div>
                </div>
              </div>
            )}

            <div>
              <Label>Reason for Rescheduling *</Label>
              <Textarea
                value={rescheduleRequestReason}
                onChange={(e) => setRescheduleRequestReason(e.target.value)}
                placeholder="Please explain why you need to reschedule (e.g., emergency, work conflict, health issue)..."
                rows={4}
                className="mt-1"
              />
            </div>

            <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
              <p className="text-xs text-blue-800">
                <span className="font-semibold">ℹ️ Note:</span> Your psychologist will review your request and contact you with alternative dates and times. Please provide at least 24 hours notice when possible.
              </p>
            </div>

            <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200">
              <p className="text-xs text-yellow-800">
                <span className="font-semibold">⚠️ Cancellation Policy:</span> Repeated last-minute cancellations may affect your ability to book future appointments. Please honor your commitments when possible.
              </p>
            </div>
          </div>
          <div className="flex gap-3 justify-end">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowRequestRescheduleModal(false);
                setRescheduleRequestConsultation(null);
                setRescheduleRequestReason('');
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSubmitRescheduleRequest}
              disabled={!rescheduleRequestReason.trim()}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Send Request
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Cancel Booking Confirmation Modal */}
      <Dialog open={showCancelModal} onOpenChange={setShowCancelModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-red-600 flex items-center gap-2">
              <AlertCircle className="w-6 h-6" /> Cancel Booking
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-red-50 p-4 rounded-lg border-2 border-red-300">
              <p className="text-sm font-semibold text-red-900 mb-2">⚠️ Warning: This action cannot be undone</p>
              <p className="text-sm text-red-800">
                Are you sure you want to cancel your consultation with <strong>{consultationToCancel?.psychologist_name}</strong>?
              </p>
              {consultationToCancel?.appointment_date && (
                <p className="text-xs text-red-700 mt-2">
                  Scheduled for: {new Date(consultationToCancel.appointment_date).toLocaleString()}
                </p>
              )}
            </div>
            
            {/* Cancellation Reason Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason for Cancellation <span className="text-red-500">*</span>
              </label>
              <textarea
                value={cancellationReason}
                onChange={(e) => setCancellationReason(e.target.value)}
                placeholder="Please explain why you're cancelling this booking..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[100px]"
                maxLength={500}
                disabled={cancellingConsultation}
              />
              <p className="text-xs text-gray-500 mt-1">
                {cancellationReason.length}/500 characters • This will be shared with the psychologist
              </p>
            </div>
            
            <p className="text-sm text-gray-600">
              The psychologist will be notified about the cancellation with your reason. You can book another consultation anytime.
            </p>
          </div>
          <div className="flex gap-3 justify-end">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowCancelModal(false);
                setConsultationToCancel(null);
                setCancellationReason('');
              }}
              disabled={cancellingConsultation}
            >
              Keep Booking
            </Button>
            <Button 
              onClick={handleConfirmCancelBooking}
              disabled={cancellingConsultation}
              className="bg-red-600 hover:bg-red-700"
            >
              {cancellingConsultation ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Cancelling...
                </>
              ) : (
                'Yes, Cancel Booking'
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Question Modal */}
      <Dialog open={showEditQuestionDialog} onOpenChange={setShowEditQuestionDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Question</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateQuestion} className="space-y-4">
            <div>
              <Label htmlFor="edit-question-subject">Subject</Label>
              <Input
                id="edit-question-subject"
                value={editQuestionSubject}
                onChange={(e) => setEditQuestionSubject(e.target.value)}
                placeholder="Brief summary of your question"
                required
              />
            </div>

            <div>
              <Label htmlFor="edit-question-message">Your Question</Label>
              <Textarea
                id="edit-question-message"
                value={editQuestionMessage}
                onChange={(e) => setEditQuestionMessage(e.target.value)}
                placeholder="Describe your question in detail..."
                rows={6}
                required
              />
            </div>

            <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
              <p className="text-xs text-blue-800">
                <span className="font-semibold">ℹ️ Note:</span> You can only edit the initial question subject and message. Replies cannot be edited through this dialog.
              </p>
            </div>

            <div className="flex justify-end space-x-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowEditQuestionDialog(false);
                  setEditingQuestion(null);
                  setEditQuestionSubject('');
                  setEditQuestionMessage('');
                }}
                disabled={updatingQuestion}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={updatingQuestion}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {updatingQuestion ? 'Updating...' : 'Update Question'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Psychologist Detail Modal */}
      <Dialog open={showPsychologistDetailModal} onOpenChange={setShowPsychologistDetailModal}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Psychologist Profile
            </DialogTitle>
          </DialogHeader>
          {selectedPsychologist && (
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                {/* Profile Photo in Modal */}
                {selectedPsychologist.proforma?.photo_url ? (
                  <img 
                    src={`${API}${selectedPsychologist.proforma.photo_url}`} 
                    alt={selectedPsychologist.name}
                    className="w-24 h-24 rounded-full object-cover border-2 border-blue-200"
                  />
                ) : (
                  <div className="w-24 h-24 bg-blue-100 rounded-full flex items-center justify-center">
                    <Brain className="w-12 h-12 text-blue-600" />
                  </div>
                )}
                <div className="flex-1">
                  <h3 className="text-2xl font-bold" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                    {selectedPsychologist.name}
                  </h3>
                  {selectedPsychologist.proforma?.specialization && (
                    <p className="text-blue-600 font-medium">{selectedPsychologist.proforma.specialization}</p>
                  )}
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded">
                {selectedPsychologist.proforma?.experience_years && (
                  <div>
                    <p className="text-sm text-gray-600">Experience</p>
                    <p className="font-semibold">{selectedPsychologist.proforma.experience_years} years</p>
                  </div>
                )}
                {selectedPsychologist.proforma?.qualifications && (
                  <div>
                    <p className="text-sm text-gray-600">Qualifications</p>
                    <p className="font-semibold">{selectedPsychologist.proforma.qualifications}</p>
                  </div>
                )}
                {selectedPsychologist.registration_number && (
                  <div className="col-span-2">
                    <p className="text-sm text-gray-600">Registration Number</p>
                    <p className="font-semibold">{selectedPsychologist.registration_number}</p>
                  </div>
                )}
              </div>
              
              {selectedPsychologist.proforma?.bio && (
                <div>
                  <h4 className="font-semibold mb-2">About</h4>
                  <p className="text-gray-700">{selectedPsychologist.proforma.bio}</p>
                </div>
              )}
              
              <div className="pt-4 border-t">
                <p className="text-sm text-gray-600 mb-3">
                  Schedule your appointment to start the Analysis:
                </p>
                
                {/* Appointment Date and Time */}
                <div className="space-y-3 mb-4 p-4 bg-blue-50 rounded">
                  <h4 className="font-semibold text-blue-900">Schedule Appointment</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="appointment-date">Date</Label>
                      <Input
                        id="appointment-date"
                        type="date"
                        value={appointmentDate}
                        onChange={(e) => setAppointmentDate(e.target.value)}
                        min={new Date().toISOString().split('T')[0]}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="appointment-time">Time</Label>
                      <Input
                        id="appointment-time"
                        type="time"
                        value={appointmentTime}
                        onChange={(e) => setAppointmentTime(e.target.value)}
                        required
                      />
                    </div>
                  </div>
                </div>
                
                {/* Optional Message */}
                <div className="space-y-2 mb-4">
                  <Label htmlFor="consultation-message">Message (Optional)</Label>
                  <Textarea
                    id="consultation-message"
                    value={consultationMessage}
                    onChange={(e) => setConsultationMessage(e.target.value)}
                    placeholder="Any specific concerns or topics you'd like to discuss..."
                    rows={3}
                  />
                </div>
                
                <Button
                  onClick={() => {
                    handleRequestConsultation('initial', selectedPsychologist.id);
                  }}
                  disabled={
                    !appointmentDate || 
                    !appointmentTime || 
                    submittingBooking ||
                    consultations.some(c => 
                      c.status === 'pending' || 
                      c.status === 'approved' || 
                      c.status === 'scheduled' || 
                      c.status === 'payment_pending'
                    )
                  }
                  className="w-full"
                  style={{ background: '#2563EB' }}
                >
                  {submittingBooking ? 'Booking...' : consultations.some(c => 
                    c.status === 'pending' || 
                    c.status === 'approved' || 
                    c.status === 'scheduled' || 
                    c.status === 'payment_pending'
                  ) ? 'Active Booking Exists' : 'Book Appointment'}
                </Button>
                
                {consultations.some(c => 
                  c.status === 'pending' || 
                  c.status === 'approved' || 
                  c.status === 'scheduled' || 
                  c.status === 'payment_pending'
                ) ? (
                  <p className="text-xs text-red-600 mt-3 p-3 bg-red-50 rounded border border-red-200">
                    ❌ You already have an active booking. Please complete or cancel your existing booking before making a new appointment.
                  </p>
                ) : (
                  <p className="text-xs text-blue-600 mt-3 p-3 bg-blue-50 rounded">
                    ⚠️ After booking approval, you'll have access to complete the 4-phase Analysis. You must finish it before your appointment date, or the appointment will be automatically cancelled.
                  </p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Consent Modal */}
      <ConsentModal 
        isOpen={showConsentModal} 
        onConsentGiven={() => setShowConsentModal(false)} 
      />

      {/* Video Call Ring Modal */}
      <VideoCallRingModal
        isOpen={!!incomingCall}
        psychologistName={incomingCall?.psychologistName}
        onAccept={handleAcceptCall}
        onDecline={handleDeclineCall}
      />

      {/* DPDPA Video Call Consent Modal */}
      <DPDPAVideoCallConsent
        isOpen={showVideoConsentModal}
        onAccept={handleVideoConsentAccept}
        onDecline={handleVideoConsentDecline}
      />

      {/* Expired Analysis Notification Modal */}
      <Dialog open={showExpiredSessionModal} onOpenChange={setShowExpiredSessionModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center text-amber-600">
              <AlertCircle className="w-6 h-6 mr-2" />
              Analysis Expired
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-gray-700 mb-3">
                {expiredSessionMessage}
              </p>
              <p className="text-sm text-gray-600">
                {expiredSessionCount === 1 
                  ? "1 session has been automatically marked as completed." 
                  : `${expiredSessionCount} sessions have been automatically marked as completed.`}
              </p>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-gray-700">
                <strong>What's next?</strong>
              </p>
              <ul className="list-disc list-inside text-sm text-gray-600 mt-2 space-y-1">
                <li>Browse available psychologists in the "Book Consultation" tab</li>
                <li>Select a psychologist and book a new consultation</li>
                <li>Complete the 4-phase analysis again for the new consultation</li>
              </ul>
            </div>
            <Button 
              onClick={() => setShowExpiredSessionModal(false)}
              className="w-full"
            >
              I Understand
            </Button>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default ClientDashboard;
